import { z } from 'zod';

import { AdPlatform } from './enums.js';
import {
  CAMPAIGN_OBJECTIVES,
  wizardFieldRequirements,
  wizardOptimizations,
  type OptimizationOption,
} from './campaign-wizard.js';
import { GOOGLE_RSA_OPTION_KEYS } from './rmf.js';
import type { CreateAdSetRequest, CreateCampaignRequest, RmfAdCreative } from './rmf.js';
import type { PublishInput } from './creative.js';

/**
 * KAMPANYA BRIEF'İ — tek girdi, çok platform (17 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bu dosya var
 * ═══════════════════════════════════════════════════════════════════════════
 * Eski sihirbaz Meta'nın nesne modelinin birebir aynasıydı: ilk adım hesap
 * seçimiydi, yani PLATFORM EN BAŞTA seçiliyordu ve ondan sonraki her soru o
 * platformun sözcükleriyle soruluyordu. El kremi satmak isteyen kullanıcı
 * önce `OUTCOME_SALES`, `OFFSITE_CONVERSIONS` ve `special_ad_categories`
 * öğrenmek zorundaydı.
 *
 * Ürün sloganı bunun tersi: "reklamlarınızı tek alandan yönetin". Yani
 * kullanıcı NE sattığını bir kez anlatır, NEREDE yayınlanacağını EN SON
 * seçer, ve platforma özgü olan ne varsa yalnız seçtiği platform için ve
 * yalnız gerçekten türetilemiyorsa sorulur.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 NORMALİZASYON BURADA, `rmf.ts`TE DEĞİL
 * ═══════════════════════════════════════════════════════════════════════════
 * `rmf.ts` açıkça "normalize etme İDDİASI YOK" diyor ve bu doğru bir karar:
 * o katman platformun KENDİ sözcüğünü taşıyor, okuma tarafı da aynı sözcüğü
 * yazıyor, aralarında eşleme yok. Bu dosya o kararı BOZMUYOR — onun ÜSTÜNE
 * biniyor.
 *
 *     BRIEF (insan dili)
 *        │  compileBrief()      ← çeviri YALNIZ burada
 *        ▼
 *     CreateCampaignRequest · CreateAdSetRequest · PublishInput
 *        │
 *        ▼
 *     POST /rmf/campaigns · /rmf/adsets · /creative/:id/publish   (değişmedi)
 *
 * Çeviriyi adapter'ın içine koymak, "hedef" kavramının iki yerde iki farklı
 * anlama gelmesi olurdu. Üst katmana koymak, aşağıdaki sözleşmenin
 * dürüstlüğünü koruyor: rmf hâlâ ne aldığını ne yaptığını tam olarak söylüyor.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Adımlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * BRIEF AKIŞININ DÖRT ADIMI — akışın TANIMI burada, bileşende değil.
 *
 * `WIZARD_STEPS` (campaign-wizard.ts) beş adımdı ve Meta'nın nesne modelini
 * yansıtıyordu: hesap → kampanya → ad set → kreatif → yayına al. İlk adımın
 * hesap seçimi olması, PLATFORMUN EN BAŞTA seçilmesi demekti ve ondan sonraki
 * her soru o platformun sözcükleriyle soruluyordu.
 *
 * Bu dörtlü sırayı TERSİNE çeviriyor: önce kullanıcının bildiği şeyler (ne
 * satıyorum, kime, ne kadar, hangi görsel), EN SON platform. Platforma özgü
 * eksikler de o son adımın İÇİNDE, yalnız işaretlenen platformun altında
 * açılıyor — beşinci bir adım doğmuyor.
 *
 * ⚠️ Adım sırası dolu daire içinde bir RAKAM ROZETİ olarak basılmaz; sessiz
 * bir üst etiket olarak yazılır ("ADIM 2/4"). Bu karar projede daha önce
 * verildi ve `WIZARD_STEPS`ten devralınıyor.
 */
export const BRIEF_STEPS = [
  /// Ne tanıtıyorsun: katalogdan ürün ya da doğrudan adres.
  'product',
  /// Ne olsun (amaç), kime (kitle), ne kadar (günlük toplam bütçe + süre).
  'goal',
  /// Reklam: kütüphaneden görseller + başlık + metin + buton.
  'ad',
  /// 🔴 NEREDE YAYINLANSIN — platform seçimi ve platforma özgü eksikler.
  'publish',
] as const;
export type BriefStep = (typeof BRIEF_STEPS)[number];

// ═══════════════════════════════════════════════════════════════════════════
// Amaç — kullanıcının sözcükleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kullanıcıya sorulan dört amaç.
 *
 * Bunlar PLATFORM SÖZCÜĞÜ DEĞİL; `GOAL_OBJECTIVE` tablosu her birini
 * platformun kendi hedefine çeviriyor. Dördü de "ne olmasını istiyorsun"
 * sorusunun cevabı ve hiçbiri diğerinin alt kümesi değil.
 *
 * ⚠️ Liste BİLEREK kısa. Meta'nın altı ODAX hedefi var, LinkedIn'in sekiz
 * `objectiveType`i; hepsini kullanıcıya açmak, bu dosyanın çözmeye çalıştığı
 * sorunu geri getirirdi. Dördü, üç platformun da karşılığı olan kesişim.
 */
/*
 * 30 Eyl 2026 (Faz 8, kampanya ajanı — denetim #13-16): BEŞİNCİ amaç `app`
 * (uygulama yükleme). Kesişim kuralı bu amaçta BİLEREK esnetildi ve dürüstçe
 * yazıldı: bugün yalnız GOOGLE açabiliyor (katalog operasyonu
 * `google.campaign.create` → APP kampanyası). Meta/TikTok/X'in uygulama
 * hedefleri sihirbaz kataloğunda YOK (`campaign-wizard.ts` → "DIŞARIDA":
 * mağaza/uygulama kimliği RMF sözleşmesinde taşınmıyor) — o platformlar bu
 * amaçta `null` ve plan satırı `GOAL_UNSUPPORTED` ile sönük. Sessizce trafiğe
 * düşürmek, kullanıcının istemediği bir şeyi satın alması olurdu.
 */
export const CAMPAIGN_GOALS = ['sales', 'traffic', 'awareness', 'leads', 'app'] as const;
export type CampaignGoal = (typeof CAMPAIGN_GOALS)[number];

/**
 * AMAÇ → PLATFORMUN KENDİ HEDEF SÖZCÜĞÜ.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `null` BİR EKSİKLİK DEĞİL, BİR CEVAP
 * ═══════════════════════════════════════════════════════════════════════════
 * `GOOGLE.awareness` `null` çünkü `google.create.ts` içindeki
 * `CREATABLE_CHANNEL_TYPES` tablosunda TEK satır var: `SEARCH`. Arama reklamı
 * niyet temellidir — birisi zaten arıyordur. Tanınırlık için arama kampanyası
 * açmak, kullanıcının parasını yanlış kanala yatırmak olur.
 *
 * `null` gelen platform, o amaç seçiliyken platform listesinde SÖNÜK durur ve
 * sebebi yazar. Sessizce `SEARCH`e düşürmek, kullanıcının seçtiğinden başka
 * bir şey satın alması demekti.
 *
 * ⚠️ Değerler UYDURULMADI:
 *   · META      → `CAMPAIGN_OBJECTIVES.META` (campaign-wizard.ts) — ve
 *                 `assertGoalCatalog()` bunu çalışma anında doğruluyor.
 *   · GOOGLE    → aynı katalog, tek satır.
 *   · LINKEDIN  → `CAMPAIGN_OBJECTIVES.LINKEDIN` (campaign-wizard.ts), o da
 *                 `LINKEDIN_OBJECTIVE_MATRIX` ile kilitli. TEKİL yazım
 *                 (`WEBSITE_CONVERSION`): şartname §2.4 kararı — çoğul yazım
 *                 yalnız eski girdide takma ad.
 *   · TIKTOK    → `CAMPAIGN_OBJECTIVES.TIKTOK` (29 Eyl 2026, Faz 5). 🔴
 *                 "Potansiyel müşteri" YERLİ `LEAD_GENERATION` (Faz 5 son
 *                 tur): reklam seti `promotion_target_type: INSTANT_PAGE`,
 *                 reklamın `page_id`si kullanıcının SEÇTİĞİ Instant Form
 *                 (`BRIEF_GAP.TIKTOK_LEAD_FORM`, `tiktok.leadform.list`).
 *                 TikTok API'si form OLUŞTURMUYOR ve LeadAds şartı Ads
 *                 Manager'da imzalanıyor — plan satırında
 *                 `TIKTOK_LEAD_FORM_IN_ADS_MANAGER` notu bunu söylüyor. Faz
 *                 5'in ilk turunda buradaki karşılık `WEB_CONVERSIONS` + piksel
 *                 `FORM` olayıydı (bağlayıcı o gün INSTANT_PAGE taşımıyordu).
 *   · TWITTER   → `CAMPAIGN_OBJECTIVES.TWITTER`. X'in lead hedefi yok (lead
 *                 kartları API'den kaldırıldı): satış ve potansiyel müşteri
 *                 `WEBSITE_CLICKS` + web olay etiketine (`WEBSITE_CONVERSIONS`)
 *                 optimizasyon; hangi olay (satın alma / kayıt) etiketin
 *                 kendisi. Aynı not.
 */
export const GOAL_OBJECTIVE: Readonly<
  Record<CampaignGoal, Readonly<Partial<Record<AdPlatform, string | null>>>>
> = {
  sales: {
    [AdPlatform.META]: 'OUTCOME_SALES',
    [AdPlatform.GOOGLE]: 'SEARCH',
    [AdPlatform.LINKEDIN]: 'WEBSITE_CONVERSION',
    [AdPlatform.TIKTOK]: 'WEB_CONVERSIONS',
    [AdPlatform.TWITTER]: 'WEBSITE_CLICKS',
  },
  traffic: {
    [AdPlatform.META]: 'OUTCOME_TRAFFIC',
    [AdPlatform.GOOGLE]: 'SEARCH',
    [AdPlatform.LINKEDIN]: 'WEBSITE_VISIT',
    [AdPlatform.TIKTOK]: 'TRAFFIC',
    [AdPlatform.TWITTER]: 'WEBSITE_CLICKS',
  },
  awareness: {
    [AdPlatform.META]: 'OUTCOME_AWARENESS',
    /**
     * 30 Eyl 2026: artık `null` DEĞİL — Demand Gen (YouTube · Discover ·
     * Gmail) ya da yalnız YouTube. Arama kampanyası YİNE açılmıyor (başlıktaki
     * gerekçe geçerli): tanınırlıkta seçilebilen Google türleri
     * `GOOGLE_TYPES_BY_GOAL.awareness` ve ikisi de katalog operasyonundan.
     */
    [AdPlatform.GOOGLE]: 'DEMAND_GEN',
    [AdPlatform.LINKEDIN]: 'BRAND_AWARENESS',
    [AdPlatform.TIKTOK]: 'REACH',
    [AdPlatform.TWITTER]: 'REACH',
  },
  leads: {
    [AdPlatform.META]: 'OUTCOME_LEADS',
    [AdPlatform.GOOGLE]: 'SEARCH',
    [AdPlatform.LINKEDIN]: 'LEAD_GENERATION',
    [AdPlatform.TIKTOK]: 'LEAD_GENERATION',
    [AdPlatform.TWITTER]: 'WEBSITE_CLICKS',
  },
  /// Uygulama yükleme — yalnız Google (bkz. `CAMPAIGN_GOALS` notu).
  app: {
    [AdPlatform.META]: null,
    [AdPlatform.GOOGLE]: 'APP',
    [AdPlatform.LINKEDIN]: null,
    [AdPlatform.TIKTOK]: null,
    [AdPlatform.TWITTER]: null,
  },
};

/**
 * AMAÇ × PLATFORM → brief'in kullandığı OPTİMİZASYON (29 Eyl 2026, Faz 5).
 *
 * `GOAL_OBJECTIVE` hedefi söylüyor ama iki amaç AYNI hedefe düşebiliyor: X'te
 * satış da trafik de `WEBSITE_CLICKS` (TikTok'ta da potansiyel müşteri Faz 5
 * son tura kadar satışla aynı `WEB_CONVERSIONS`ti). Farkı optimizasyon (ve
 * olay) yapıyor; 29 Eyl'e kadar
 * brief hedefin İLK seçeneğini (`[0]`) alıyordu ve bu, iki amacı aynı
 * kampanyaya çevirirdi.
 *
 * Tabloda olmayan platform → katalogdaki İLK seçenek (LinkedIn'de hedef
 * başına tek seçenek var; Google'da hiç yok — iki alan gönderilemez).
 *
 * `customEventType` OLAY: Meta'da piksel olayı, TikTok'ta `optimization_event`
 * (kullanıcı `BRIEF_GAP.TIKTOK_OPTIMIZATION_EVENT` ile değiştirebilir). X'te
 * olay web olay ETİKETİNİN kendisi — burada yok.
 *
 * `assertGoalCatalog()` her satırın katalogda gerçekten bulunduğunu doğruluyor.
 */
export interface GoalOptimizationPick {
  optimizationGoal: string;
  customEventType?: string;
}

export const GOAL_OPTIMIZATION: Readonly<
  Record<CampaignGoal, Readonly<Partial<Record<AdPlatform, GoalOptimizationPick>>>>
> = {
  sales: {
    [AdPlatform.META]: { optimizationGoal: 'OFFSITE_CONVERSIONS', customEventType: 'PURCHASE' },
    [AdPlatform.TIKTOK]: { optimizationGoal: 'CONVERT', customEventType: 'SHOPPING' },
    [AdPlatform.TWITTER]: { optimizationGoal: 'WEBSITE_CONVERSIONS' },
  },
  traffic: {
    [AdPlatform.META]: { optimizationGoal: 'LINK_CLICKS' },
    [AdPlatform.TIKTOK]: { optimizationGoal: 'CLICK' },
    [AdPlatform.TWITTER]: { optimizationGoal: 'LINK_CLICKS' },
  },
  awareness: {
    [AdPlatform.META]: { optimizationGoal: 'REACH' },
    [AdPlatform.TIKTOK]: { optimizationGoal: 'REACH' },
    [AdPlatform.TWITTER]: { optimizationGoal: 'MAX_REACH' },
  },
  leads: {
    /* 🔴 Varsayılan ANINDA FORM (Faz 5 kararı: potansiyel müşteri amacı form
       sorar). Web sitesi formu isteyen `meta.leadMode = website` seçer →
       `OFFSITE_CONVERSIONS` + `LEAD` (`briefOptimization`). */
    [AdPlatform.META]: { optimizationGoal: 'LEAD_GENERATION' },
    /* Instant Form — piksel/olay YOK; form reklamda (`TIKTOK_LEAD_FORM`). */
    [AdPlatform.TIKTOK]: { optimizationGoal: 'LEAD_GENERATION' },
    [AdPlatform.TWITTER]: { optimizationGoal: 'WEBSITE_CONVERSIONS' },
  },
  /// Yalnız Google — orada optimizasyon alanı yok (teklif stratejisi katalogda).
  app: {},
};

/// Meta "potansiyel müşteri" yolları — `BRIEF_GAP.META_LEAD_MODE`in değerleri.
export const META_LEAD_MODES = ['instantForm', 'website'] as const;
export type MetaLeadMode = (typeof META_LEAD_MODES)[number];

/// Web sitesi formu yolu: katalogdaki piksel seçeneği (`OUTCOME_LEADS[0]`).
const META_WEBSITE_LEADS: GoalOptimizationPick = {
  optimizationGoal: 'OFFSITE_CONVERSIONS',
  customEventType: 'LEAD',
};

/**
 * Bu amaç bu platformda açılabilir mi.
 *
 * `undefined` (tabloda platform hiç yok) ile `null` (tabloda var ama karşılığı
 * yok) AYNI cevabı veriyor — ikisi de "hayır" — ama ayrı duruyorlar çünkü
 * arayüz ikisine farklı cümle yazıyor: biri "bu platform desteklenmiyor",
 * diğeri "bu AMAÇ bu platformda olmuyor".
 */
export function objectiveFor(goal: CampaignGoal, platform: AdPlatform): string | null {
  return GOAL_OBJECTIVE[goal][platform] ?? null;
}

/// Meta'nın seçili "potansiyel müşteri" yolu — verilmemişse anında form.
export function metaLeadMode(options: Readonly<Record<string, unknown>> = {}): MetaLeadMode {
  return options[BRIEF_GAP.META_LEAD_MODE] === 'website' ? 'website' : 'instantForm';
}

/**
 * Brief'in bu amaç × platformda gönderdiği optimizasyon seçeneği.
 *
 * Katalogdan OKUNUR (`wizardOptimizations`), seçim `GOAL_OPTIMIZATION`dan;
 * olay (`customEventType`) amaca göre ezilir. `options` yerleşimin cevapları:
 * Meta "web sitesi formu" yolu ve TikTok'un seçilen dönüşüm olayı buradan.
 *
 * `null`: amaç bu platformda yok ya da katalogda optimizasyon yok (Google —
 * iki alan orada GÖNDERİLEMEZ, ve bu da bir cevap).
 */
export function briefOptimization(
  goal: CampaignGoal,
  platform: AdPlatform,
  options: Readonly<Record<string, unknown>> = {},
): OptimizationOption | null {
  const objective = objectiveFor(goal, platform);
  if (objective === null) return null;
  const catalog = wizardOptimizations(platform, objective);

  let pick = GOAL_OPTIMIZATION[goal][platform];
  if (platform === AdPlatform.META && goal === 'leads' && metaLeadMode(options) === 'website') {
    pick = META_WEBSITE_LEADS;
  }
  const option = pick
    ? catalog.find((candidate) => candidate.optimizationGoal === pick.optimizationGoal)
    : catalog[0];
  if (!option) return null;

  const chosenEvent =
    platform === AdPlatform.TIKTOK && option.requiresPromotedObject
      ? optionText(options, BRIEF_GAP.TIKTOK_OPTIMIZATION_EVENT)
      : null;
  const event = chosenEvent ?? pick?.customEventType ?? option.customEventType;
  return event ? { ...option, customEventType: event } : option;
}

/**
 * 🔴 SÖZLEŞME İLE GERÇEK ARASINDAKİ KİLİT.
 *
 * `GOAL_OBJECTIVE` elle yazılmış bir tablo; `CAMPAIGN_OBJECTIVES` sihirbazın
 * gerçekten sunduğu hedef kataloğu. İkisi ayrışırsa kullanıcı, katalogda
 * olmayan bir hedefle kampanya açmaya çalışır ve reddi SON adımda, platformdan
 * alır.
 *
 * Bu fonksiyon o ayrışmayı erken yakalıyor ve testte çağrılıyor. Elle kopya
 * liste tutmanın bedeli projede daha önce ödendi (`ACTION_TYPES_WITH_EXECUTOR`
 * ile aynı ders): liste yazılır, gerçek değişir, arada üç faz boyunca ölü bir
 * yüzey kalır.
 *
 * LINKEDIN 29 Eyl 2026'dan beri katalogda ve burada DENETLENİYOR: katalog
 * yokken `compileBrief` LinkedIn'e boş optimizasyon gönderiyor ve her reklam
 * seti adapter'da düşüyordu.
 */
export function assertGoalCatalog(): string[] {
  const problems: string[] = [];

  for (const goal of CAMPAIGN_GOALS) {
    for (const [platform, objective] of Object.entries(GOAL_OBJECTIVE[goal])) {
      if (objective === null) continue;
      /* 30 Eyl 2026: Google'ın arama DIŞI türleri sihirbaz kataloğunda değil,
         katalog operasyonunda (`google.campaign.create`) — denetimi o türlerin
         kendi kopyasına karşı (`GOOGLE_CATALOG_CAMPAIGN_TYPES`, zincir
         testinde operasyon şemasıyla kilitli). */
      if (platform === AdPlatform.GOOGLE && objective !== 'SEARCH') {
        if (!(GOOGLE_CATALOG_CAMPAIGN_TYPES as readonly string[]).includes(objective)) {
          problems.push(`GOAL_OBJECTIVE.${goal}.GOOGLE = "${objective}" — google.campaign.create türlerinde yok`);
        }
        const types = GOOGLE_TYPES_BY_GOAL[goal];
        if (types.length === 0 || !types.every((type) => type === 'SEARCH' || googleCatalogTypeOf(type) !== null)) {
          problems.push(`GOOGLE_TYPES_BY_GOAL.${goal} — boş ya da katalog türü olmayan satır`);
        }
        continue;
      }
      const catalog = CAMPAIGN_OBJECTIVES[platform];
      /// Katalogda hiç yoksa bu platform liste gösterilen platformlardan değil.
      if (!catalog) continue;
      if (!catalog.some((option) => option.value === objective)) {
        problems.push(
          `GOAL_OBJECTIVE.${goal}.${platform} = "${objective}" — CAMPAIGN_OBJECTIVES.${platform} içinde yok`,
        );
      }
    }

    /* Faz 5: seçilen optimizasyon da katalogda olmalı — yoksa brief hedefin
       ilk seçeneğine SESSİZCE düşmez, `briefOptimization` null döner ve reklam
       seti optimizasyonsuz gider (Meta/TikTok/X'te ret). */
    for (const [platform, pick] of Object.entries(GOAL_OPTIMIZATION[goal])) {
      const objective = GOAL_OBJECTIVE[goal][platform as AdPlatform];
      if (!objective || !pick) {
        problems.push(`GOAL_OPTIMIZATION.${goal}.${platform} — GOAL_OBJECTIVE satırı yok`);
        continue;
      }
      const options = wizardOptimizations(platform, objective);
      if (!options.some((option) => option.optimizationGoal === pick.optimizationGoal)) {
        problems.push(
          `GOAL_OPTIMIZATION.${goal}.${platform} = "${pick.optimizationGoal}" — CAMPAIGN_OBJECTIVES.${platform}.${objective} içinde yok`,
        );
      }
    }
  }

  const website = wizardOptimizations(AdPlatform.META, 'OUTCOME_LEADS').find(
    (option) => option.optimizationGoal === META_WEBSITE_LEADS.optimizationGoal,
  );
  if (!website) problems.push('META_WEBSITE_LEADS — OUTCOME_LEADS kataloğunda piksel seçeneği yok');

  return problems;
}

// ═══════════════════════════════════════════════════════════════════════════
// Bütçe seviyesi — platform başına FARKLI ve bu ÖLÇÜLDÜ
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Günlük bütçe HANGİ SEVİYEYE yazılır.
 *
 * Bu tablo bir tercih değil, üç adapter'ın gövdelerinden okundu:
 *
 *   META      → CAMPAIGN. Meta kampanya bütçesi (CBO) varken ad set'e ayrı
 *               bütçe verilemez; ikisi birden gönderilirse ad set reddedilir.
 *               Adapter bütçe gördüğünde `bid_strategy`yi kendisi
 *               `LOWEST_COST_WITHOUT_CAP` yapıyor (meta.connector.ts:1638),
 *               yani brief'in ayrıca teklif stratejisi sorması gerekmiyor.
 *   GOOGLE    → CAMPAIGN. Google'da `campaign_budget` kampanyanın alanı.
 *   LINKEDIN  → ADSET. `linkedin.write.ts` `createCampaign` gövdesinde bütçe
 *               ALANI YOK (account, name, objectiveType, status); bütçe
 *               `createAdSet` içinde `dailyBudget` olarak gidiyor. LinkedIn'in
 *               kendi modelinde bizim "kampanya"mız Campaign Group.
 *
 *   TIKTOK    → ADSET (29 Eyl 2026, Faz 5). `tiktok.build.ts` → `adGroupBody`:
 *               CBO kapalıyken reklam setinde bütçe ZORUNLU
 *               (ADGROUP_BUDGET_REQUIRED); kampanya bütçesiz gidince
 *               `BUDGET_MODE_INFINITE` oluyor. CBO açmak ayrı bir karar
 *               (`platformOptions.budgetOptimization`) ve brief sormuyor.
 *   TWITTER   → CAMPAIGN. X'in günlük bütçesi kampanyanın alanı
 *               (`daily_budget_amount_local_micro`); reklam setinde yalnız
 *               toplam bütçe ve teklif var.
 *
 * 🔴 Tek bir seviye seçip hepsine uygulamak, LinkedIn'de BÜTÇESİZ bir kampanya
 * açmak olurdu: varlık oluşur, yayına alınır ve hiç harcamaz. Hiçbir ekran da
 * bunu söylemez.
 */
export const BUDGET_LEVEL: Readonly<Partial<Record<AdPlatform, 'CAMPAIGN' | 'ADSET'>>> = {
  [AdPlatform.META]: 'CAMPAIGN',
  [AdPlatform.GOOGLE]: 'CAMPAIGN',
  [AdPlatform.LINKEDIN]: 'ADSET',
  [AdPlatform.TIKTOK]: 'ADSET',
  [AdPlatform.TWITTER]: 'CAMPAIGN',
};

/**
 * `durationDays` → bitiş tarihi HANGİ seviyeye yazılır.
 *
 *   GOOGLE → CAMPAIGN. Google tarihi yalnız kampanyada tanıyor
 *            (`end_date_time`); ad group'a gönderilen `endTime`
 *            `FIELD_NOT_SUPPORTED_ON_PLATFORM` ile reddediliyor ve 29 Eyl
 *            2026'ya kadar süre seçilen her Google yerleşimi ad group
 *            adımında düşüyordu.
 *   Diğerleri → ADSET (Meta `end_time`, LinkedIn `runSchedule.end`).
 *   TIKTOK / TWITTER → ADSET, AÇIKÇA yazıldı: iki adapter de kampanyaya
 *            verilen bitişi REDDEDİYOR (TikTok'ta takvim reklam setinde, X'te
 *            `end_time` reklam setinde).
 */
export const END_TIME_LEVEL: Readonly<Partial<Record<AdPlatform, 'CAMPAIGN' | 'ADSET'>>> = {
  [AdPlatform.GOOGLE]: 'CAMPAIGN',
  [AdPlatform.TIKTOK]: 'ADSET',
  [AdPlatform.TWITTER]: 'ADSET',
};

/**
 * TOPLAM bütçeyi platformlara böl.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 NEDEN TOPLAM SORULUYOR — "500₺" üç platformda 1500₺ demekti
 * ═══════════════════════════════════════════════════════════════════════════
 * Her platform kendi günlük bütçesini ayrı harcıyor. Kullanıcının girdiği tek
 * sayıyı her platforma AYNEN göndermek, harcamayı platform sayısıyla çarpmak
 * olurdu ve bunu fark edeceği yer ertesi günün ekstresi.
 *
 * Bölüm kuruş kaybetmiyor: tam bölünmeyen artık İLK platforma ekleniyor, yani
 * parçaların toplamı her zaman girilen toplama EŞİT.
 *
 * ⚠️ Eşit bölüm bir BAŞLANGIÇ değeri; kullanıcı son adımda satır satır
 * değiştirebiliyor. Hangi platformun daha çok hak ettiğini gün 0'da bilmenin
 * yolu yok — o soruyu `budget_redistribute` kuralı veriyle cevaplıyor
 * (`autoOptimizeBudget`).
 */
export function splitDailyBudget(total: number, parts: number): number[] {
  if (parts < 1) return [];
  /// Kuruşta çalış: 500 / 3 = 166,666… gibi bir sayı Meta'ya giderken
  /// yuvarlanır ve toplam tutmaz.
  const minor = Math.round(total * 100);
  const base = Math.floor(minor / parts);
  const remainder = minor - base * parts;

  return Array.from({ length: parts }, (_, index) => {
    const share = base + (index < remainder ? 1 : 0);
    return share / 100;
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Google kampanya türleri — arama DIŞI (30 Eyl 2026, Faz 8 · denetim #13-16)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Brief'in açabildiği Google türleri.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden iki ayrı yol
 * ═══════════════════════════════════════════════════════════════════════════
 * RMF adapter'ı (`google.create.ts`) BİLEREK yalnız `SEARCH` açıyor —
 * PMax "asset group", Demand Gen görsel/video reklamı, Alışveriş Merchant
 * Center istiyor ve kampanya → ad group → reklam üçlüsü onlarda çalışmıyor.
 * O dosyanın kararı değişmedi. Bu türler katalog operasyonundan
 * (`google.campaign.create`) gidiyor: kampanya, bütçe, konum ve çocuklar TEK
 * atomik istekte, kampanya PAUSED doğuyor. Yayınlayıcı
 * (`CampaignBriefService.runPlacement`) `CompiledPlacement.catalog` doluysa
 * üçlü yerine onu koşuyor.
 *
 *   SEARCH           arama (bugünkü yol, AYNEN)
 *   PERFORMANCE_MAX  tek varlık grubu — başlık/uzun başlık/açıklama/işletme
 *                    adı/logo + brief görselleri ORANINA göre yuvalarda
 *   DEMAND_GEN       YouTube · Discover · Gmail — çoklu varlık görsel reklamı
 *   YOUTUBE          yalnız YouTube kanallı Demand Gen + video reklamı (Google
 *                    Ads API VIDEO kampanyası OLUŞTURMAYA izin vermiyor —
 *                    `google.campaign.create` açıklaması; Demand Gen'in YouTube
 *                    kanal kontrolü onun API karşılığı)
 *   SHOPPING         bağlı Merchant Center'ın tüm ürünleri
 *   APP              uygulama yükleme (yalnız `app` amacında; seçilmiyor)
 */
export const GOOGLE_BRIEF_CAMPAIGN_TYPES = [
  'SEARCH',
  'PERFORMANCE_MAX',
  'DEMAND_GEN',
  'YOUTUBE',
  'SHOPPING',
  'APP',
] as const;
export type GoogleBriefCampaignType = (typeof GOOGLE_BRIEF_CAMPAIGN_TYPES)[number];

/**
 * `google.campaign.create` → `type` enum'unun KOPYASI (`platform-ops` kök
 * pakete girmiyor — bkz. "Platform sözcük tabloları"). Kilit:
 * `brief-google-types-chain.spec.ts` operasyon şemasıyla karşılaştırıyor.
 */
export const GOOGLE_CATALOG_CAMPAIGN_TYPES = ['SEARCH', 'DISPLAY', 'SHOPPING', 'PERFORMANCE_MAX', 'DEMAND_GEN', 'APP'] as const;

/**
 * Amaç → seçilebilen Google türleri; İLKİ varsayılan. Satış/trafik/lead'de
 * varsayılan hâlâ ARAMA (bugünkü davranış değişmesin); tanınırlıkta arama
 * YOK (niyet temelli kanal — `GOAL_OBJECTIVE` başlığı).
 */
export const GOOGLE_TYPES_BY_GOAL: Readonly<Record<CampaignGoal, readonly GoogleBriefCampaignType[]>> = {
  sales: ['SEARCH', 'PERFORMANCE_MAX', 'SHOPPING', 'DEMAND_GEN'],
  traffic: ['SEARCH', 'PERFORMANCE_MAX', 'DEMAND_GEN', 'YOUTUBE', 'SHOPPING'],
  awareness: ['DEMAND_GEN', 'YOUTUBE'],
  leads: ['SEARCH', 'PERFORMANCE_MAX', 'DEMAND_GEN'],
  app: ['APP'],
};

/** Brief türü → katalog operasyonunun `type`ı; `SEARCH` (RMF yolu) → `null`. */
export function googleCatalogTypeOf(
  type: GoogleBriefCampaignType,
): 'PERFORMANCE_MAX' | 'DEMAND_GEN' | 'SHOPPING' | 'APP' | null {
  switch (type) {
    case 'PERFORMANCE_MAX':
    case 'DEMAND_GEN':
    case 'SHOPPING':
    case 'APP':
      return type;
    case 'YOUTUBE':
      return 'DEMAND_GEN';
    default:
      return null;
  }
}

/**
 * Yerleşimin Google türü — cevap amaçta geçerliyse o, değilse amacın
 * varsayılanı. (Geçersiz cevabı gap denetimi zaten `missing` sayıyor;
 * burada yalnız derleyicinin hangi yoldan gideceği.)
 */
export function googleCampaignType(
  goal: CampaignGoal,
  options: Readonly<Record<string, unknown>> = {},
): GoogleBriefCampaignType {
  const allowed = GOOGLE_TYPES_BY_GOAL[goal];
  const raw = options[BRIEF_GAP.GOOGLE_CAMPAIGN_TYPE];
  return typeof raw === 'string' && (allowed as readonly string[]).includes(raw)
    ? (raw as GoogleBriefCampaignType)
    : (allowed[0] ?? 'SEARCH');
}

/**
 * Mağaza adresinden uygulama kimliği — kullanıcıya ikinci kez sorulmasın.
 *   play.google.com/store/apps/details?id=com.ornek.app → GOOGLE_APP_STORE
 *   apps.apple.com/…/id1234567890                        → APPLE_APP_STORE
 * Tanınmayan adres `null` (alan sorulur).
 */
export function appFromStoreUrl(
  url: string,
): { appId: string; store: 'GOOGLE_APP_STORE' | 'APPLE_APP_STORE' } | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (host === 'play.google.com') {
    const id = parsed.searchParams.get('id')?.trim();
    return id && /^[A-Za-z][\w.]{1,199}$/.test(id) ? { appId: id, store: 'GOOGLE_APP_STORE' } : null;
  }
  if (host === 'apps.apple.com' || host === 'itunes.apple.com') {
    const match = /\/id(\d{5,15})(?:\/|$)/.exec(parsed.pathname);
    return match?.[1] ? { appId: match[1], store: 'APPLE_APP_STORE' } : null;
  }
  return null;
}

export const GOOGLE_APP_STORES = ['GOOGLE_APP_STORE', 'APPLE_APP_STORE'] as const;

// ═══════════════════════════════════════════════════════════════════════════
// Platforma özgü eksikler — "ayrı, sonra sorulan" kısım
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Brief'in taşıyamadığı, platformun ZORUNLU tuttuğu alanlar.
 *
 * Bu liste kısa tutuldu ve kısa kalmalı: her yeni satır, kullanıcının son
 * adımda cevaplamak zorunda kalacağı bir soru demek. Bir alan buraya ancak
 * "türetilemiyor VE platform onsuz reddediyor" ikisi birden doğruysa girer.
 *
 * Türetilebilen hiçbir şey burada DEĞİL: kampanya adı brief'ten üretiliyor,
 * ad set adı kampanya adından, `startTime` yok (varlık zaten PAUSED doğuyor),
 * `specialAdCategories` boş dizi (kullanıcı hiçbir şey beyan etmedi).
 *
 * 29 Eyl 2026 (Faz 5) liste büyüdü ama kural AYNI: yeni satırların çoğu ya
 * İSTEĞE BAĞLI (`min: 0` — varsayılanı olan seçim: yerleşim, fon kaynağı) ya
 * da KOŞULLU (yalnız brief platforma sığmıyorsa: yaş kovası, metin uzunluğu;
 * yalnız belirli amaçta: lead formu, piksel). Her yerleşim satırında
 * kullanıcıya düşen zorunlu soru hâlâ bir-iki tane.
 */
export const BRIEF_GAP = {
  /**
   * Meta Sayfa kimliği.
   *
   * YENİ açılmış bir ad set'in İLK reklamında zorunlu: `publishSchema.
   * identityRef` boş bırakılırsa adapter hedefin altındaki mevcut bir
   * reklamdan devralmaya çalışıyor ve yeni ad set'te devralınacak bir şey yok.
   * Kullanıcının bildiği bir numara, modelin ya da bizim tahmin edebileceğimiz
   * bir şey değil — `/integrations/connections/:id/identities` listeliyor.
   */
  META_IDENTITY: 'meta.identityRef',
  /**
   * Meta dönüşüm pikseli.
   *
   * 🔴 BU ALAN KATALOGDAN TÜRETİLİYOR, elle karar verilmiyor: seçilen amacın
   * optimizasyon seçeneği `requiresPromotedObject: true` taşıyorsa piksel
   * zorunlu (`OFFSITE_CONVERSIONS` piksel olmadan Meta tarafından reddediliyor).
   * Yani "satış ve potansiyel müşteri amaçlarında piksel iste" kuralı BURAYA
   * YAZILMADI — `wizardOptimizations()` okundu.
   */
  META_PIXEL: 'meta.pixelId',
  /**
   * 🔴 GOOGLE ANAHTAR KELİMELERİ — onsuz kampanya HİÇ GÖSTERİM ALMAZ.
   *
   * Arama kampanyası kelimelerle eşleşerek yayınlanır. Kelimesiz bir ad group
   * teknik olarak geçerli bir nesnedir, Google onu memnuniyetle oluşturur ve
   * ömrü boyunca sıfır gösterim alır. Kullanıcı kampanyayı panelde "ACTIVE"
   * görür ve neden hiçbir şey olmadığını hiçbir ekrandan öğrenemez.
   *
   * Bu yüzden zorunlu ve bu yüzden `google.create.ts` pozitif kelime yazacak
   * hâle getirildi (adapter'da yalnız `addNegativeKeyword` vardı).
   */
  GOOGLE_KEYWORDS: 'google.keywords',
  /**
   * Google RSA başlıkları — en az 3.
   *
   * Brief tek başlık taşıyor. Eksikleri aynı metni tekrarlayarak doldurmak
   * Google'ın kabul ettiği bir hile ama reklamın performansını düşürür ve
   * kullanıcı bunu ASLA göremez (`GOOGLE_RSA_OPTION_KEYS` başlığı).
   */
  GOOGLE_HEADLINES: 'google.headlines',
  /// Google RSA açıklamaları — en az 2. Aynı gerekçe.
  GOOGLE_DESCRIPTIONS: 'google.descriptions',
  /// LinkedIn organizasyon URN'i. `META_IDENTITY` ile aynı sınıf, farklı biçim.
  LINKEDIN_IDENTITY: 'linkedin.identityRef',

  // ── 29 Eyl 2026 (Faz 5) — lead formu, TikTok, X ─────────────────────────
  /**
   * Meta "potansiyel müşteri" yolu: `instantForm` (varsayılan — Meta içinde
   * anında form, piksel gerekmez) ya da `website` (sitedeki form, piksel
   * `LEAD` olayı). İSTEĞE BAĞLI (`min: 0`); diğer iki alan bunun değerine
   * bağlı (`BriefGap.when`).
   */
  META_LEAD_MODE: 'meta.leadMode',
  /**
   * 🔴 Meta anında formu — lead reklamının VAR OLUŞ sebebi. Değer
   * `BriefLeadFormValue`: mevcut form (`meta.leadform.list`, Sayfaya bağlı) ya
   * da brief'ten YENİ form (`meta.leadform.create` — gizlilik politikası adresi
   * zorunlu; teşekkür ekranı siteye bağlantı taşıyorsa varış adresi → ADMIN).
   */
  META_LEAD_FORM: 'meta.leadForm',
  /// LinkedIn lead gen formu — LEAD_GENERATION reklamında ZORUNLU
  /// (`leadgenCallToAction`). Aynı değer biçimi; oluşturma `linkedin.leadform.create`.
  LINKEDIN_LEAD_FORM: 'linkedin.leadForm',
  /**
   * LinkedIn reklam seti dili (iki harf, ör. `tr`). İSTEĞE BAĞLI: tek dilli
   * ülkede adapter türetiyor; ÇOK DİLLİ ülkede (BE, CH, CA…) adapter
   * reddediyor (LOCALE_REQUIRED) ve YENİ lead formu oluşturulurken zorunlu
   * (form dili kampanya diliyle aynı olmalı).
   */
  LINKEDIN_LANGUAGE: 'linkedin.language',
  /// Brief yaş aralığı LinkedIn'in dört kovasına oturmuyorsa: dar/geniş seçim.
  LINKEDIN_AGE_RANGE: 'linkedin.ageRange',
  /**
   * 🔴 TikTok yayıncı kimliği — Spark Ads. TikTok yerleşiminde özel kimlik
   * 15 Oca 2026'dan beri YASAK; reklam bir TikTok hesabı (TT_USER /
   * BC_AUTH_TT) ya da yetki kodu (AUTH_CODE) adına yayınlanıyor. Liste
   * `GET /integrations/connections/:id/identities` (değer `TT_USER:<id>`).
   */
  TIKTOK_IDENTITY: 'tiktok.identityRef',
  /// TikTok pikseli — dönüşüm (`CONVERT`) optimizasyonunda ZORUNLU.
  TIKTOK_PIXEL: 'tiktok.pixelId',
  /// TikTok optimizasyon olayı (`SHOPPING`, `FORM`…). Varsayılan amaçtan.
  TIKTOK_OPTIMIZATION_EVENT: 'tiktok.optimizationEvent',
  /// TikTok yerleşimi: otomatik (TikTok dahil) · yalnız TikTok · yalnız Pangle.
  TIKTOK_PLACEMENT: 'tiktok.placement',
  /// Brief yaş aralığı TikTok'un sabit gruplarına oturmuyorsa.
  TIKTOK_AGE_RANGE: 'tiktok.ageRange',
  /**
   * TikTok reklam metni — yalnız brief metni TikTok'a SIĞMIYORSA sorulur
   * (1–100 karakter, Çince/Japonca/Korece 2 sayılır, emoji YOK). TikTok'ta
   * başlık alanı yok; metin gövde metninden geliyor.
   */
  TIKTOK_AD_TEXT: 'tiktok.adText',
  /**
   * 🔴 TikTok Instant Form (lead formu) — "potansiyel müşteri" amacında
   * ZORUNLU; değer formun `page_id`si (sayısal). Yalnız MEVCUT form seçilir
   * (`source`: `tiktok.leadform.list`, yayımlanmış formlar): TikTok API'si
   * form oluşturmuyor, yenisi TikTok Ads Manager'da yapılıyor
   * (`BRIEF_NOTICE.TIKTOK_LEAD_FORM_IN_ADS_MANAGER`).
   */
  TIKTOK_LEAD_FORM: 'tiktok.leadForm',
  /**
   * X fon kaynağı (ödeme yöntemi). İSTEĞE BAĞLI: hesapta TEK aktif kaynak varsa
   * adapter onu seçiyor; birden çoksa adapter "hangisi" diye reddediyor
   * (FUNDING_INSTRUMENT_AMBIGUOUS) — liste `x.funding.list`.
   */
  X_FUNDING_INSTRUMENT: 'x.fundingInstrumentId',
  /// X web olay etiketi (X Pixel olayı) — dönüşüm optimizasyonunda ZORUNLU.
  X_WEB_EVENT_TAG: 'x.webEventTag',
  /// Gönderinin atılacağı X kullanıcısı. İSTEĞE BAĞLI: verilmezse hesabın FULL kullanıcısı.
  X_IDENTITY: 'x.identityRef',
  /// Brief yaş aralığı X'in kovalarından BİRİNE TAM eşit değilse (X tek kova alıyor).
  X_AGE_BUCKET: 'x.ageBucket',
  /// X yerleşimleri. İSTEĞE BAĞLI — varsayılan `ALL_ON_TWITTER`.
  X_PLACEMENTS: 'x.placements',
  /// X web sitesi kartı başlığı — brief başlığı 70 karakteri AŞIYORSA sorulur.
  X_CARD_TITLE: 'x.cardTitle',
  /// X gönderi metni — brief metni 280 (ağırlıklı) karakteri AŞIYORSA sorulur.
  X_POST_TEXT: 'x.postText',

  // ── 30 Eyl 2026 (Faz 8, denetim #13-16) — kitle, Google türleri, uygulama ──
  //
  // KİTLE ALANLARI hep İSTEĞE BAĞLI (`min: 0`) ve platform kimliği taşıyor:
  // brief'in ortak kitlesi (ülke/yaş/cinsiyet) DEĞİŞMEDİ — ilgi alanı, özel/
  // benzer kitle ve şehir/bölge her platformun KENDİ listesinden seçiliyor
  // (`search` / çoklu `source`). Değer biçimi `BriefPickedItem[]` (arayüz adı
  // da saklıyor) ya da düz kimlik dizisi (model).
  /// Meta ilgi alanları — `meta.targeting.search` (adinterest), `flexible_spec`e.
  META_INTERESTS: 'meta.interests',
  /// Meta özel + benzer kitleler — `meta.audience.list`, `custom_audiences`a.
  META_AUDIENCES: 'meta.audiences',
  /// Meta şehir/bölge — `meta.targeting.search` (adgeolocation); kapsadığı ülke düşer.
  META_LOCATIONS: 'meta.locations',
  /**
   * 🔴 Google kampanya TÜRÜ. `SEARCH` RMF yolundan (bugünkü akış, AYNEN);
   * Performance Max / Demand Gen / YouTube / Alışveriş / Uygulama katalog
   * operasyonundan (`google.campaign.create`, TEK atomik istek). Seçenekler
   * amaca göre (`GOOGLE_TYPES_BY_GOAL`), varsayılan listenin ilki.
   */
  GOOGLE_CAMPAIGN_TYPE: 'google.campaignType',
  /// Google şehir/bölge — `google.geo.suggest`; kapsadığı ülke düşer.
  GOOGLE_LOCATIONS: 'google.locations',
  /// Google kitle listeleri (yeniden pazarlama / müşteri eşleştirme) — `google.audience.search_segments`.
  GOOGLE_USER_LISTS: 'google.userLists',
  /// Google ilgi/pazardaki segmentler — `google.audience.search_segments`.
  GOOGLE_INTERESTS: 'google.interests',
  /// Performance Max / Demand Gen / YouTube işletme adı (≤25).
  GOOGLE_BUSINESS_NAME: 'google.businessName',
  /// Uzun başlıklar (≤90) — Performance Max / Demand Gen video.
  GOOGLE_LONG_HEADLINES: 'google.longHeadlines',
  /// Kare logo — hesaptaki görsel varlık (`google.asset.list` IMAGE).
  GOOGLE_LOGO: 'google.logo',
  /// YouTube videoları — hesaptaki YOUTUBE_VIDEO varlıkları (yalnız YouTube türü).
  GOOGLE_YOUTUBE_VIDEOS: 'google.youtubeVideos',
  /// Merchant Center — `google.merchant.list_links` (Alışveriş).
  GOOGLE_MERCHANT: 'google.merchantId',
  /// Uygulama kimliği (Android paket adı / iOS sayısal kimlik) — mağaza adresinden türetilebiliyor.
  GOOGLE_APP_ID: 'google.appId',
  /// Uygulama mağazası — mağaza adresinden türetilebiliyor.
  GOOGLE_APP_STORE: 'google.appStore',
  /// TikTok özel/benzer kitleleri — `tiktok.audience.list` (CUSTOM), `audience_ids`e.
  TIKTOK_AUDIENCES: 'tiktok.audiences',
  /// TikTok ilgi kategorileri — `tiktok.targeting.search` (INTEREST_CATEGORY), `interest_category_ids`e.
  TIKTOK_INTERESTS: 'tiktok.interests',
} as const;
export type BriefGapField = (typeof BRIEF_GAP)[keyof typeof BRIEF_GAP];

/**
 * Girdinin BİÇİMİ — arayüz bunu okuyup alanı çiziyor.
 *
 *   `text`     tek satır (`maxLength` varsa sınır).
 *   `list`     çoklu değer (kelimeler, başlıklar) — en az `min`.
 *   `identity` `GET /integrations/connections/:id/identities` seçici
 *              (Meta Sayfaları, TikTok Spark kimlikleri).
 *   `choice`   sabit seçenekler (`choices`); `multiple` ise dizi.
 *   `source`   bir OKUMA operasyonundan dolan seçici (`source`):
 *              `POST /platform-ops/run {opId, params, adAccountId}` →
 *              `result.data[listPath][]` → değer `valueField`, etiket `labelField`.
 *   `leadForm` mevcut formu seç (`source`) YA DA brief'ten yeni form
 *              (`leadForm` meta verisi) — değer `BriefLeadFormValue`.
 *   `search`   (30 Eyl 2026) ARAMALI kaynak: aynı okuma operasyonu, ama liste
 *              kullanıcının yazdığı sözcükle çekiliyor (`source.queryParam`) —
 *              ilgi alanı, şehir, kitle segmenti gibi binlerce satırlık
 *              kümeler. Her zaman çoklu; değer `BriefPickedItem[]`.
 *
 * `source` + `multiple: true` (30 Eyl 2026) → çoklu seçim; değer yine
 * `BriefPickedItem[]` (ya da düz kimlik dizisi).
 */
export type BriefGapKind = 'text' | 'list' | 'identity' | 'choice' | 'source' | 'leadForm' | 'search';

/**
 * Seçilmiş bir liste öğesi — `search` ve çoklu `source` alanlarının değeri.
 *
 * Arayüz ADI da saklıyor: arama sonucu yeniden çekilemeyebilir (sözcük
 * değişti) ve kart "3 ilgi alanı" değil "Koşu, Yoga, Pilates" demeli. `extra`
 * derleyicinin ihtiyacı olan ek alanlar (`source.keep`): Meta konumunun türü
 * (şehir/bölge) ve ülkesi gibi. Model düz kimlik dizisi de yazabiliyor —
 * `briefPickedItems` ikisini aynı biçime getiriyor.
 */
export interface BriefPickedItem {
  id: string;
  name?: string;
  /// `source.keep` alanları (ör. `{ type: 'city', countryCode: 'TR' }`).
  extra?: Record<string, string>;
}

/// Bir alanın en fazla kaç seçimi olabilir (platform sınırlarının altında, yönetilebilir).
export const BRIEF_PICK_MAX = 50;

export interface BriefGapChoice {
  value: string;
  /// i18n anahtarı; `params` ICU parametreleri (yaş aralığı gibi sayısal etiketler).
  labelKey: string;
  params?: Record<string, string | number>;
}

export interface BriefGapSource {
  /// OKUMA operasyonu (`kind: 'read'`) — yan etkisiz.
  opId: string;
  params: Record<string, unknown>;
  /// Başka bir cevaptan gelen parametre: `{ pageId: 'meta.identityRef' }` —
  /// o alan dolmadan liste çekilemez.
  paramsFromGaps?: Readonly<Record<string, BriefGapField>>;
  /// `result.data` içindeki dizinin yolu; `''` = `data`nın kendisi dizi.
  /// Noktalı yol olabilir (30 Eyl 2026).
  listPath: string;
  /**
   * Satırdaki değer/etiket alanı. NOKTALI YOL olabilir (30 Eyl 2026): Google
   * GAQL satırları iç içe (`user_list.resource_name`, `asset.name`).
   */
  valueField: string;
  labelField: string;
  /// Etiketin yanında sönük basılan ikinci alan (tür, ülke, kitle alt türü).
  detailField?: string;
  /// `search`: sorgunun gideceği parametre ve biçimi (`keywords: ['koşu']` gibi dizi).
  queryParam?: string;
  queryAsArray?: boolean;
  /// `search`: en kısa sorgu (Google segment araması 2 ister).
  minQuery?: number;
  /**
   * Seçilen öğede SAKLANACAK ek alanlar — `{ bizimAd: 'satırdakiYol' }`.
   * Derleyici bunları okuyor (Meta konumunun `type`ı `cities`/`regions`
   * ayrımı için şart).
   */
  keep?: Readonly<Record<string, string>>;
  /**
   * `true`: satırın doğrulanmamış ham platform yanıtından okunduğu biliniyor
   * (TikTok araç uçları) — alan adı canlıda farklıysa liste boş gelir ve
   * alan elle kimlik girişine düşer. Dürüstlük işareti, davranış değiştirmez.
   */
  unverifiedShape?: boolean;
}

/**
 * Noktalı yol okuyucu — `source` satırları için (web aynı fonksiyonu
 * kullanıyor; iki kopya ayrışırdı).
 */
export function briefSourceValue(row: unknown, path: string): unknown {
  if (!path) return row;
  let node: unknown = row;
  for (const part of path.split('.')) {
    if (!node || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node;
}

/**
 * Okuma sonucunun `data`sı → seçenek satırları. `listPath` yoksa `data`nın
 * kendisi dizi. Değeri olmayan satır atlanıyor.
 */
export function briefSourceRows(
  source: BriefGapSource,
  data: unknown,
): { value: string; label: string; detail: string | null; extra: Record<string, string> }[] {
  const list = source.listPath ? briefSourceValue(data, source.listPath) : data;
  if (!Array.isArray(list)) return [];
  const text = (value: unknown): string | null =>
    typeof value === 'string' && value.trim() ? value.trim() : typeof value === 'number' ? String(value) : null;
  return list.flatMap((row) => {
    const value = text(briefSourceValue(row, source.valueField));
    if (value === null) return [];
    const extra: Record<string, string> = {};
    for (const [key, path] of Object.entries(source.keep ?? {})) {
      const kept = text(briefSourceValue(row, path));
      if (kept !== null) extra[key] = kept;
    }
    return [
      {
        value,
        label: text(briefSourceValue(row, source.labelField)) ?? value,
        detail: source.detailField ? text(briefSourceValue(row, source.detailField)) : null,
        extra,
      },
    ];
  });
}

/**
 * `search` / çoklu `source` cevabı → seçilmiş öğeler. Dize (model), dize
 * dizisi ya da `{id,name,…}` nesneleri kabul ediliyor; bozuk öğe `null`
 * döndürür (cevap GEÇERSİZ — sessizce budanmıyor).
 */
export function briefPickedItems(raw: unknown): BriefPickedItem[] | null {
  if (raw === undefined || raw === null) return [];
  const list = Array.isArray(raw) ? raw : [raw];
  const out: BriefPickedItem[] = [];
  for (const item of list) {
    if (typeof item === 'string' || typeof item === 'number') {
      const id = String(item).trim();
      if (!id) return null;
      out.push({ id });
      continue;
    }
    if (!item || typeof item !== 'object') return null;
    const record = item as Record<string, unknown>;
    const id = typeof record.id === 'string' || typeof record.id === 'number' ? String(record.id).trim() : '';
    if (!id || id.length > 300) return null;
    const extra: Record<string, string> = {};
    const nested = record.extra && typeof record.extra === 'object' ? (record.extra as Record<string, unknown>) : {};
    for (const [key, value] of Object.entries({ ...record, ...nested })) {
      if (key === 'id' || key === 'name' || key === 'extra') continue;
      if (typeof value === 'string' && value.length <= 120) extra[key] = value;
    }
    out.push({
      id,
      ...(typeof record.name === 'string' && record.name.trim() ? { name: record.name.trim().slice(0, 300) } : {}),
      ...(Object.keys(extra).length > 0 ? { extra } : {}),
    });
  }
  const unique = out.filter((item, index) => out.findIndex((other) => other.id === item.id) === index);
  return unique.length > BRIEF_PICK_MAX ? null : unique;
}

/// Brief'ten yeni lead formu kurarken sorulabilecek alanlar.
export const BRIEF_LEAD_QUESTIONS = [
  'FULL_NAME',
  'EMAIL',
  'PHONE',
  'CITY',
  'COUNTRY',
  'COMPANY_NAME',
  'JOB_TITLE',
  'WORK_EMAIL',
] as const;
export type BriefLeadQuestion = (typeof BRIEF_LEAD_QUESTIONS)[number];
export const BRIEF_LEAD_DEFAULT_QUESTIONS: readonly BriefLeadQuestion[] = ['FULL_NAME', 'EMAIL', 'PHONE'];

export interface BriefLeadFormMeta {
  /// Yeni form operasyonu (`meta.leadform.create` / `linkedin.leadform.create`).
  createOpId: string;
  /**
   * 🔴 Oluşturma ne zaman YÖNETİCİ ister — REST `/platform-ops/run` kuralı
   * (geri alınamaz ya da hassas yazma → ADMIN), katalogdan okundu:
   *   `always`              LinkedIn — gizlilik adresi her formda VARIŞ ADRESİ.
   *   `withThankYouMessage` Meta — teşekkür ekranı siteye bağlantı taşıyınca.
   * Arayüz o durumda "yeni form"u daha düşük rolde KAPALI gösterir.
   */
  createRequiresAdmin: 'always' | 'withThankYouMessage';
  questionChoices: readonly BriefLeadQuestion[];
  defaultQuestions: readonly BriefLeadQuestion[];
  /// Gizlilik adresi `https` olmak ZORUNDA mı (LinkedIn evet).
  httpsPrivacyPolicy: boolean;
  /// Yeni formda ek metin alanları ve sınırları (LinkedIn); brief değerleri
  /// sınırı aşıyorsa kullanıcı kısaltmalı — sessizce kırpılmıyor.
  textFields?: Readonly<{ headline: number; description: number; thankYouMessage: number }>;
  /// Yeni form `BRIEF_GAP.LINKEDIN_LANGUAGE` cevabını ister.
  needsLanguage: boolean;
}

/** Bir eksik alanın arayüzde nasıl görüneceği. */
export interface BriefGap {
  field: BriefGapField;
  platform: AdPlatform;
  kind: BriefGapKind;
  /**
   * Zorunluluk: `0` = İSTEĞE BAĞLI (boş bırakılabilir; verilirse doğrulanır),
   * `1+` = zorunlu (liste alanlarında en az kaç değer).
   */
  min: number;
  /// i18n anahtarı — düz metin DEĞİL. Sunucu anahtar gönderir, ekran çevirir.
  labelKey: string;
  hintKey: string;
  /// `text` için karakter tavanı (platformun sayımı derleyicide).
  maxLength?: number;
  /// `choice` seçenekleri.
  choices?: readonly BriefGapChoice[];
  /// `choice` çoklu mu (dizi değer).
  multiple?: boolean;
  /// Boş bırakıldığında derleyicinin kullandığı değer — arayüz ön seçer.
  defaultValue?: string | readonly string[];
  /// `source` / `leadForm` için liste kaynağı.
  source?: BriefGapSource;
  /// `leadForm` için yeni form meta verisi.
  leadForm?: BriefLeadFormMeta;
  /**
   * Alan YALNIZ başka bir cevap bu değerdeyken geçerli (ör. Meta piksel
   * yalnız `meta.leadMode = website`). Cevap boşsa o alanın `defaultValue`su
   * esas alınır. Koşul tutmuyorsa arayüz alanı GİZLER, derleyici sormaz.
   *
   * `in` (30 Eyl 2026): değer listedekilerden BİRİ olmalı — Google'da bir
   * alan birkaç kampanya türünde soruluyor (işletme adı: PMax, Demand Gen,
   * YouTube). `equals` ve `in` birlikte verilmez.
   */
  when?: { field: BriefGapField; equals?: string; in?: readonly string[] };
}

/**
 * BİLGİLENDİRME — engellemiyor ama söylenmeden geçilemez.
 *
 * Uyarı ile eksik alan arasındaki fark şu: eksik alan cevaplanmadan devam
 * EDİLEMEZ, uyarı ise kullanıcının bilerek kabul ettiği bir şey.
 */
export const BRIEF_NOTICE = {
  /**
   * 🔴 Google seçilen görselleri KULLANMIYOR.
   *
   * Adapter'ın açabildiği tek tür `SEARCH`, yani Responsive Search Ad — salt
   * metin. Kullanıcı kütüphaneden üç kreatif seçip Meta + Google derse
   * Google'a giden reklamda o görsellerin hiçbiri olmaz.
   *
   * Bunu SEÇİMDEN ÖNCE söylemek şart: sonradan söylemek, kullanıcının parasını
   * beklediğinden farklı bir reklama harcaması demek.
   */
  GOOGLE_TEXT_ONLY: 'google.textOnly',
  /// Seçilen amacın bu platformda karşılığı yok (`GOAL_OBJECTIVE` → `null`).
  GOAL_UNSUPPORTED: 'goal.unsupported',
  /// `canPlatformCreate()` false — adapter bu platformda varlık açamıyor.
  CANNOT_CREATE: 'platform.cannotCreate',
  /**
   * Payına düşen günlük bütçe, platformun alt sınırının altında kalabilir.
   *
   * Sabit bir sayı YAZILMADI ve yazılamaz: Meta `min_daily_budget`i HESAP
   * BAŞINA döndürüyor (By Nini hesabında 47,88 TRY) ve para birimine göre
   * değişiyor. Uyarı, bölünmüş payın "çok küçük" olduğu durumu işaret ediyor;
   * kesin reddi platform veriyor.
   */
  BUDGET_SHARE_LOW: 'budget.shareLow',

  // ── 29 Eyl 2026 (Faz 5) ─────────────────────────────────────────────────
  /**
   * 🔴 ENGELLER — brief'te ülke yok ama bu platform ülkesiz reklam seti
   * açamıyor (`wizardFieldRequirements().countriesRequired`: Meta, LinkedIn,
   * TikTok, X). Önceden kampanya açılıyor, reklam seti adımında düşüyordu
   * (yetim kampanya). Kullanıcı 2. adımda ülke seçer.
   */
  COUNTRIES_REQUIRED: 'audience.countriesRequired',
  /// ENGELLER — yaş aralığı platformun HİÇBİR kovasıyla karşılanamıyor
  /// (ör. LinkedIn'de 18 altı). Aralık değişmeli.
  AGE_UNSUPPORTED: 'audience.ageUnsupported',
  /**
   * ENGELLER — seçilen kreatiflerden en az biri bu platformun bu yoldan
   * kabul ettiği türde değil (`params.count`). Meta: video bu yoldan
   * yayınlanamıyor (yalnız görsel); TikTok: TikTok/otomatik yerleşim video
   * istiyor (görsel yalnız Pangle); X/LinkedIn: görsel ya da video.
   */
  MEDIA_UNSUPPORTED: 'creative.mediaUnsupported',
  /// Bilgi — TikTok yerleşimi video istiyor; seçimdeki görseller yalnız
  /// Pangle yerleşiminde yayınlanabilir (`params.count`). Engellemiyor:
  /// kullanıcı `tiktok.placement = PANGLE` seçebilir.
  TIKTOK_VIDEO_REQUIRED: 'tiktok.videoRequired',
  /// ENGELLER — TikTok kampanya/reklam seti adında emoji kabul etmiyor.
  TEXT_EMOJI: 'text.emojiNotAllowed',
  /**
   * Bilgi — bu platformda "potansiyel müşteri" amacı yerel form yerine web
   * sitesi dönüşümüne (X olay etiketi) optimize ediyor. Yalnız X (TikTok Faz 5
   * son turdan beri yerli Instant Form). Gerekçe `GOAL_OBJECTIVE` başlığında.
   */
  LEADS_AS_WEBSITE_CONVERSION: 'leads.websiteConversion',
  /**
   * Bilgi — TikTok "potansiyel müşteri": reklam TikTok Instant Form'uyla
   * açılır ve form listeden SEÇİLİR; TikTok API'si form oluşturmuyor. Yeni
   * form ve TikTok'un Lead Ads şartları TikTok Ads Manager'da (Varlıklar →
   * Instant Page → Instant Form). Engellemiyor: formu olan kullanıcı devam eder.
   */
  TIKTOK_LEAD_FORM_IN_ADS_MANAGER: 'tiktok.leadFormInAdsManager',
  /**
   * ENGELLER (yalnız derlemede, 30 Eyl 2026) — seçilen Google türü görsel
   * istiyor ama seçimde uygun ORANLI görsel yok: Performance Max en az bir
   * yatay (1.91:1) + bir kare (1:1), Demand Gen en az bir yatay ya da kare.
   * Google oranı %1 toleransla denetliyor (`google/ops/scope.ts` →
   * `assertImage`); kampanya atomik istekte düşerdi. `params.count` uygun
   * görsel sayısı. Plan satırında YOK: tür yerleşimin cevabı, plan onu bilmiyor.
   */
  GOOGLE_ASSETS_MISSING: 'google.assetsMissing',
} as const;
export type BriefNoticeCode = (typeof BRIEF_NOTICE)[keyof typeof BRIEF_NOTICE];

export interface BriefNotice {
  code: BriefNoticeCode;
  platform: AdPlatform;
  /**
   * Bu uyarı platformu SEÇİLEMEZ mi kılıyor.
   *
   * `GOAL_UNSUPPORTED`, `CANNOT_CREATE`, `COUNTRIES_REQUIRED`,
   * `AGE_UNSUPPORTED`, `MEDIA_UNSUPPORTED`, `TEXT_EMOJI` engelliyor;
   * `GOOGLE_TEXT_ONLY`, `BUDGET_SHARE_LOW`, `TIKTOK_VIDEO_REQUIRED`,
   * `LEADS_AS_WEBSITE_CONVERSION`, `TIKTOK_LEAD_FORM_IN_ADS_MANAGER`
   * engellemiyor — kullanıcı okuyup devam edebilir.
   */
  blocking: boolean;
  /// ICU parametreleri (ör. `count` — uygun olmayan kreatif sayısı).
  params?: Record<string, string | number>;
}

/**
 * Kreatifin medya türü — derleyici saf kaldığı için DIŞARIDAN geliyor
 * (servis `Creative.mimeType`tan okuyor). Verilmezse medya denetimi YAPILMAZ.
 */
export type BriefCreativeMedia = 'image' | 'video' | 'other';

/** `planBrief` / `compileBrief`in dış bağlamı. */
export interface BriefContext {
  /// Kreatif kimliği → medya türü. Bilinmeyen kimlik denetlenmez (yayın ucu
  /// zaten CREATIVE_NOT_FOUND döner).
  media?: Readonly<Record<string, BriefCreativeMedia>>;
  /**
   * Kreatif kimliği → piksel boyutu (30 Eyl 2026). Google'ın görsel isteyen
   * türleri (Performance Max, Demand Gen) görseli ORANINA göre yuvaya
   * koyuyor; boyut bilinmezse derleyici o görseli hiçbir yuvaya koyamaz.
   * Verilmezse (ajan kartının kuru denetimi) görsel denetimi YAPILMAZ.
   */
  dims?: Readonly<Record<string, { width: number | null; height: number | null }>>;
}

/**
 * Görselin Google yuvası — oran %1 toleransla (`google/ops/scope.ts` →
 * `SLOT_RATIO` + `assertImage` ile AYNI eşik; zincir testi kilitliyor).
 */
export type GoogleImageSlot = 'LANDSCAPE' | 'SQUARE' | 'PORTRAIT' | 'TALL_PORTRAIT';
export const GOOGLE_IMAGE_SLOT_RATIO: Readonly<Record<GoogleImageSlot, number>> = {
  LANDSCAPE: 1.91,
  SQUARE: 1,
  PORTRAIT: 0.8,
  TALL_PORTRAIT: 9 / 16,
};

export function googleImageSlot(width: number | null, height: number | null): GoogleImageSlot | null {
  if (!width || !height) return null;
  const actual = width / height;
  for (const [slot, expected] of Object.entries(GOOGLE_IMAGE_SLOT_RATIO) as [GoogleImageSlot, number][]) {
    if (Math.abs(actual - expected) / expected <= 0.01) return slot;
  }
  return null;
}

/** MIME türü → brief medya sınıfı. */
export function briefMediaOf(mimeType: string | null | undefined): BriefCreativeMedia {
  const mime = (mimeType ?? '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  return 'other';
}

// ═══════════════════════════════════════════════════════════════════════════
// Platform sözcük tabloları (29 Eyl 2026, Faz 5)
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 `platform-ops/*.ops.ts`ten İÇE AKTARILMIYOR, yazılıyor: `platform-ops`
// kök pakete bilerek girmiyor (yüzlerce Zod şeması; panelin her sayfası kök
// paketi yüklüyor — `platform-ops/index.ts` başlığı). Kopyalar API
// tarafındaki zincir testlerinde asıllarıyla KİLİTLİ
// (`brief-tiktok-chain.spec.ts`, `brief-x-chain.spec.ts`).

/// TikTok yerleşim seçimi. `AUTOMATIC` TikTok'u KAPSAR (video ister).
export const TIKTOK_BRIEF_PLACEMENTS = ['AUTOMATIC', 'TIKTOK', 'PANGLE'] as const;
export type TikTokBriefPlacement = (typeof TIKTOK_BRIEF_PLACEMENTS)[number];

/// TikTok piksel optimizasyon olayları (şartname §2.3 "conversion").
export const TIKTOK_BRIEF_EVENTS = [
  'SHOPPING',
  'ON_WEB_CART',
  'ON_WEB_DETAIL',
  'INITIATE_ORDER',
  'ADD_BILLING',
  'FORM',
  'ON_WEB_REGISTER',
  'ON_WEB_SUBSCRIBE',
  'CONSULT',
  'DOWNLOAD_START',
] as const;

/// X yerleşimleri — reklam gönderisi tanıtımı için anlamlı olanlar.
export const X_BRIEF_PLACEMENTS = [
  'ALL_ON_TWITTER',
  'TWITTER_TIMELINE',
  'TWITTER_SEARCH',
  'TWITTER_PROFILE',
  'TWITTER_REPLIES',
  'PUBLISHER_NETWORK',
] as const;

/// X yaş kovaları — `x.ops.ts` → `X_AGE_BUCKETS` ile AYNI (kilit: brief-x-chain.spec).
export const X_BRIEF_AGE_BUCKETS = [
  'AGE_13_TO_24',
  'AGE_13_TO_34',
  'AGE_13_TO_49',
  'AGE_13_TO_54',
  'AGE_OVER_13',
  'AGE_18_TO_34',
  'AGE_18_TO_49',
  'AGE_18_TO_54',
  'AGE_OVER_18',
  'AGE_21_TO_34',
  'AGE_21_TO_49',
  'AGE_21_TO_54',
  'AGE_OVER_21',
  'AGE_25_TO_49',
  'AGE_25_TO_54',
  'AGE_OVER_25',
  'AGE_35_TO_49',
  'AGE_35_TO_54',
  'AGE_OVER_35',
  'AGE_OVER_50',
] as const;

/**
 * Brief CTA'sı → TikTok CTA sözcüğü (`TIKTOK_CTAS`). İki farklı yazım var:
 * `DOWNLOAD` → `DOWNLOAD_NOW`, `BOOK_TRAVEL` → `BOOK_NOW`. Tabloda olmayan
 * değer AYNEN gider (TikTok reddederse ret görünür, sessizce değişmez).
 */
export const TIKTOK_CTA_FOR_BRIEF: Readonly<Record<string, string>> = {
  LEARN_MORE: 'LEARN_MORE',
  SHOP_NOW: 'SHOP_NOW',
  SIGN_UP: 'SIGN_UP',
  CONTACT_US: 'CONTACT_US',
  DOWNLOAD: 'DOWNLOAD_NOW',
  BOOK_TRAVEL: 'BOOK_NOW',
};

/**
 * Reklamı lead FORMUYLA açılabilen platformlar (reklam adımı `leadFormRef`
 * taşıyor). TikTok Faz 5 son turda girdi: bağlayıcı `leadFormRef`i reklamın
 * `page_id`sine yazıyor ve yalnız Instant Form lead setinde kabul ediyor
 * (`tiktok.rmf.ts` → `assertLeadFormAdGroup`). Listede olmayan platforma form
 * gönderilirse `PlatformActionService` REDDEDİYOR (sessiz formsuz yayın yok).
 */
export const BRIEF_LEAD_FORM_PLATFORMS: readonly AdPlatform[] = [
  AdPlatform.META,
  AdPlatform.LINKEDIN,
  AdPlatform.TIKTOK,
];

// ── Metin ─────────────────────────────────────────────────────────────────

/// `tiktok.build.ts` → `EMOJI_RE` ile aynı aralıklar.
const EMOJI_RE = /[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2B00}-\u{2BFF}\u{FE0F}\u{200D}]/u;
/// Çince/Japonca/Korece — TikTok ve X bunları İKİ karakter sayıyor.
const WIDE_CHAR_RE = /[぀-ヿ㐀-鿿가-힯豈-﫿]/u;

export const TIKTOK_AD_TEXT_MAX = 100;
export const X_CARD_TITLE_MAX = 70;
export const X_POST_TEXT_MAX = 280;

export function hasEmoji(text: string): boolean {
  return EMOJI_RE.test(text);
}

/// Platform sayımı: geniş karakter 2, diğerleri 1.
export function weightedTextLength(text: string): number {
  let length = 0;
  for (const char of text) length += WIDE_CHAR_RE.test(char) ? 2 : 1;
  return length;
}

/// TikTok reklam metni kuralı (`assertAdText`): 1–100 (ağırlıklı), emojisiz.
export function tiktokTextFits(text: string): boolean {
  const value = text.trim();
  return value.length > 0 && !hasEmoji(value) && weightedTextLength(value) <= TIKTOK_AD_TEXT_MAX;
}

// ── Yaş kovaları ──────────────────────────────────────────────────────────

/// Brief'in yaş tavanı "65+" — açık uç.
const OPEN_AGE = 65;

interface AgeSpan {
  min: number;
  /// `OPEN_AGE` = üst sınırsız.
  max: number;
}

/**
 * Kovalı yaş hedeflemesi: aralık ANCAK kova sınırlarına oturuyorsa
 * çevrilebiliyor (adapter'lar genişletmiyor — kullanıcının seçmediği yaşa para).
 * `floor` altı başlangıç: TikTok'ta 13 zaten tabandır; LinkedIn 18 altını
 * gösteremediği için alt sınırı 18'e ÇEKİYOR (erişilebilir kitle aynı).
 */
interface AgeLadder {
  starts: readonly number[];
  /// Kapalı uçlar; son kovanın ucu `OPEN_AGE`.
  ends: readonly number[];
  floor: number;
}

/// `tiktok.rmf.ts` → `AGE_BUCKETS` (13-17 … 55+).
const TIKTOK_AGE_LADDER: AgeLadder = { starts: [13, 18, 25, 35, 45, 55], ends: [17, 24, 34, 44, 54], floor: 13 };
/// `linkedin.ops.ts` → `LINKEDIN_AGE_BUCKETS` (18-24, 25-34, 35-54, 55+).
const LINKEDIN_AGE_LADDER: AgeLadder = { starts: [18, 25, 35, 55], ends: [24, 34, 54], floor: 18 };

function spanLabel(span: AgeSpan): string {
  return span.max >= OPEN_AGE ? `${span.min}+` : `${span.min}–${span.max}`;
}

function requestedSpan(audience: BriefAudience, floor: number): AgeSpan | null {
  if (audience.ageMin === undefined && audience.ageMax === undefined) return null;
  const max = audience.ageMax === undefined || audience.ageMax >= OPEN_AGE ? OPEN_AGE : audience.ageMax;
  return { min: Math.max(audience.ageMin ?? floor, floor), max };
}

function ladderAligned(ladder: AgeLadder, span: AgeSpan): boolean {
  return ladder.starts.includes(span.min) && (span.max >= OPEN_AGE || ladder.ends.includes(span.max));
}

/// Dar (içte kalan en geniş) ve geniş (kapsayan en dar) hizalı aralıklar.
function ladderChoices(ladder: AgeLadder, span: AgeSpan): AgeSpan[] {
  const ends = [...ladder.ends, OPEN_AGE];
  const out: AgeSpan[] = [];
  const innerMin = ladder.starts.find((start) => start >= span.min);
  const innerMax = ends.filter((end) => end <= span.max).at(-1);
  if (innerMin !== undefined && innerMax !== undefined && innerMin <= innerMax) {
    out.push({ min: innerMin, max: innerMax });
  }
  const outerMin = ladder.starts.filter((start) => start <= span.min).at(-1) ?? ladder.starts[0];
  const outerMax = ends.find((end) => end >= span.max) ?? OPEN_AGE;
  if (outerMin !== undefined && !out.some((c) => c.min === outerMin && c.max === outerMax)) {
    out.push({ min: outerMin, max: outerMax });
  }
  return out;
}

function parseXBucket(bucket: string): AgeSpan | null {
  const range = /^AGE_(\d+)_TO_(\d+)$/.exec(bucket);
  if (range) return { min: Number(range[1]), max: Number(range[2]) };
  const over = /^AGE_OVER_(\d+)$/.exec(bucket);
  return over ? { min: Number(over[1]), max: OPEN_AGE } : null;
}

const X_BUCKET_SPANS: readonly { bucket: string; span: AgeSpan }[] = X_BRIEF_AGE_BUCKETS.map((bucket) => ({
  bucket,
  span: parseXBucket(bucket) ?? { min: 0, max: 0 },
}));

/**
 * Yaş aralığı bu platformda nasıl çözülüyor.
 *
 *   `none`        yaş hedeflemesi yok (brief sormadı) ya da platform yaş almıyor.
 *   `aligned`     brief aralığı AYNEN gidiyor.
 *   `choose`      kovaya oturmuyor — `choices`tan biri seçilmeli (gap).
 *   `unsupported` hiçbir kova karşılamıyor (LinkedIn'de 18 altı).
 */
type AgePlan =
  | { kind: 'none' }
  | { kind: 'aligned'; span: AgeSpan; bucket?: string }
  | { kind: 'choose'; choices: { value: string; span: AgeSpan }[] }
  | { kind: 'unsupported' };

function agePlan(platform: AdPlatform, audience: BriefAudience): AgePlan {
  if (platform === AdPlatform.TIKTOK || platform === AdPlatform.LINKEDIN) {
    const ladder = platform === AdPlatform.TIKTOK ? TIKTOK_AGE_LADDER : LINKEDIN_AGE_LADDER;
    if (audience.ageMax !== undefined && audience.ageMax < ladder.floor) return { kind: 'unsupported' };
    const span = requestedSpan(audience, ladder.floor);
    if (!span) return { kind: 'none' };
    if (ladderAligned(ladder, span)) return { kind: 'aligned', span };
    const choices = ladderChoices(ladder, span).map((choice) => ({
      value: `${choice.min}-${choice.max}`,
      span: choice,
    }));
    return choices.length > 0 ? { kind: 'choose', choices } : { kind: 'unsupported' };
  }

  if (platform === AdPlatform.TWITTER) {
    const span = requestedSpan(audience, 13);
    if (!span) return { kind: 'none' };
    const exact = X_BUCKET_SPANS.find((row) => row.span.min === span.min && row.span.max === span.max);
    if (exact) return { kind: 'aligned', span: exact.span, bucket: exact.bucket };
    const inner = X_BUCKET_SPANS.filter((row) => row.span.min >= span.min && row.span.max <= span.max).sort(
      (a, b) => b.span.max - b.span.min - (a.span.max - a.span.min) || a.span.min - b.span.min,
    )[0];
    const outer = X_BUCKET_SPANS.filter((row) => row.span.min <= span.min && row.span.max >= span.max).sort(
      (a, b) => a.span.max - a.span.min - (b.span.max - b.span.min) || b.span.min - a.span.min,
    )[0];
    const choices = [inner, outer]
      .filter((row): row is { bucket: string; span: AgeSpan } => row !== undefined)
      .filter((row, index, all) => all.findIndex((other) => other.bucket === row.bucket) === index)
      .map((row) => ({ value: row.bucket, span: row.span }));
    return choices.length > 0 ? { kind: 'choose', choices } : { kind: 'unsupported' };
  }

  return { kind: 'none' };
}

const AGE_GAP_FIELD: Readonly<Partial<Record<AdPlatform, BriefGapField>>> = {
  [AdPlatform.TIKTOK]: BRIEF_GAP.TIKTOK_AGE_RANGE,
  [AdPlatform.LINKEDIN]: BRIEF_GAP.LINKEDIN_AGE_RANGE,
  [AdPlatform.TWITTER]: BRIEF_GAP.X_AGE_BUCKET,
};

/**
 * Derleyicinin göndereceği yaş sınırları. `null` = cevap geçersiz/eksik
 * (gap denetimi bunu zaten yakalıyor). X'te sınırlar HER ZAMAN kovanın
 * kendisi — adapter tam eşleşme arıyor (`ageBucket`).
 */
function resolvedAge(
  platform: AdPlatform,
  audience: BriefAudience,
  options: Readonly<Record<string, unknown>>,
): { ageMin?: number; ageMax?: number } | null {
  const plan = agePlan(platform, audience);
  const toBounds = (span: AgeSpan) => ({
    ageMin: span.min,
    ...(span.max >= OPEN_AGE ? (platform === AdPlatform.TWITTER ? {} : { ageMax: OPEN_AGE }) : { ageMax: span.max }),
  });
  switch (plan.kind) {
    case 'none':
      return {
        ...(audience.ageMin !== undefined ? { ageMin: audience.ageMin } : {}),
        ...(audience.ageMax !== undefined ? { ageMax: audience.ageMax } : {}),
      };
    case 'aligned':
      return platform === AdPlatform.TWITTER
        ? toBounds(plan.span)
        : {
            ...(audience.ageMin !== undefined ? { ageMin: audience.ageMin } : {}),
            ...(audience.ageMax !== undefined ? { ageMax: audience.ageMax } : {}),
          };
    case 'choose': {
      const field = AGE_GAP_FIELD[platform];
      const answer = field ? options[field] : undefined;
      const choice = plan.choices.find((row) => row.value === answer);
      return choice ? toBounds(choice.span) : null;
    }
    default:
      return null;
  }
}

// ── Lead formu ────────────────────────────────────────────────────────────

/**
 * Lead formu alanının DEĞERİ.
 *
 *   `{mode:'existing', id}` → mevcut form (Meta form kimliği, LinkedIn
 *                             `urn:li:adForm:…` ya da sayısal kimlik).
 *   `{mode:'create', …}`    → brief'ten yeni form, yayınlamada oluşturulur.
 *   Düz dize                → `existing` kısaltması.
 */
export const briefLeadFormValueSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('existing'), id: z.string().trim().min(1).max(128) }),
  z.object({
    mode: z.literal('create'),
    /// Meta ve LinkedIn ikisi de ZORUNLU tutuyor; LinkedIn yalnız https.
    privacyPolicyUrl: z.url().max(2000),
    questions: z.array(z.enum(BRIEF_LEAD_QUESTIONS)).min(1).max(BRIEF_LEAD_QUESTIONS.length).optional(),
    /// Formun başlığı/açıklaması/teşekkür metni — verilmezse brief'ten.
    headline: z.string().trim().min(1).max(60).optional(),
    description: z.string().trim().min(1).max(160).optional(),
    thankYouMessage: z.string().trim().min(1).max(300).optional(),
  }),
]);
export type BriefLeadFormValue = z.infer<typeof briefLeadFormValueSchema>;

/// LinkedIn formunun metin sınırları (`linkedin.leadform.create` şeması).
export const LINKEDIN_LEAD_FORM_LIMITS = { headline: 60, description: 160, thankYouMessage: 300 } as const;

export function parseLeadFormValue(raw: unknown): BriefLeadFormValue | null {
  if (typeof raw === 'string') {
    const id = raw.trim();
    return id.length > 0 ? { mode: 'existing', id } : null;
  }
  const parsed = briefLeadFormValueSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/// LinkedIn form kimliği → URN (`linkedin.leadform.list` sayısal `id` döndürüyor).
function linkedInFormUrn(id: string): string {
  return id.startsWith('urn:li:adForm:') ? id : `urn:li:adForm:${id}`;
}

/// Değer bu platformda kullanılabilir mi (biçim + LinkedIn metin sınırları).
function leadFormValueValid(platform: AdPlatform, value: BriefLeadFormValue, brief: CampaignBrief): boolean {
  if (value.mode === 'existing') {
    return platform === AdPlatform.LINKEDIN
      ? /^(urn:li:adForm:)?\d{1,25}$/.test(value.id)
      : /^\d{1,25}$/.test(value.id);
  }
  if (platform !== AdPlatform.LINKEDIN) return true;
  if (!value.privacyPolicyUrl.startsWith('https://')) return false;
  const headline = value.headline ?? brief.headline;
  const description = value.description ?? brief.bodyText;
  return (
    headline.length <= LINKEDIN_LEAD_FORM_LIMITS.headline &&
    description.length <= LINKEDIN_LEAD_FORM_LIMITS.description
  );
}

const META_LEAD_QUESTION: Readonly<Record<BriefLeadQuestion, string>> = {
  FULL_NAME: 'FULL_NAME',
  EMAIL: 'EMAIL',
  PHONE: 'PHONE',
  CITY: 'CITY',
  COUNTRY: 'COUNTRY',
  COMPANY_NAME: 'COMPANY_NAME',
  JOB_TITLE: 'JOB_TITLE',
  WORK_EMAIL: 'WORK_EMAIL',
};

/**
 * LinkedIn soruları: hazır profil alanı + soru metni (API `question` metnini
 * zorunlu tutuyor). Ad-soyad LinkedIn'de İKİ alan. Metin yalnız tr/en elle
 * yazıldı (projenin elle yazılan iki dili); diğer dillerde İngilizce —
 * hazır alanlar LinkedIn'de üyenin profilinden dolduruluyor, metin etiket.
 */
const LINKEDIN_LEAD_QUESTION: Readonly<Record<BriefLeadQuestion, readonly (readonly [string, string, string])[]>> = {
  FULL_NAME: [
    ['FIRST_NAME', 'First name', 'Ad'],
    ['LAST_NAME', 'Last name', 'Soyad'],
  ],
  EMAIL: [['EMAIL', 'Email', 'E-posta']],
  PHONE: [['PHONE_NUMBER', 'Phone number', 'Telefon']],
  CITY: [['CITY', 'City', 'Şehir']],
  COUNTRY: [['COUNTRY', 'Country', 'Ülke']],
  COMPANY_NAME: [['COMPANY_NAME', 'Company name', 'Şirket']],
  JOB_TITLE: [['JOB_TITLE', 'Job title', 'Unvan']],
  WORK_EMAIL: [['WORK_EMAIL', 'Work email', 'İş e-postası']],
};

const LINKEDIN_THANK_YOU: Readonly<Record<string, string>> = {
  tr: 'Teşekkürler! En kısa sürede sizinle iletişime geçeceğiz.',
  en: "Thank you! We'll be in touch soon.",
};

/**
 * Derlenmiş lead formu — reklam adımından ÖNCE yayınlayıcı çözüyor.
 *
 *   `existing` → `ref` reklamın `leadFormRef`i (yayın girdisinde zaten dolu).
 *                TikTok'ta YALNIZ bu (Instant Form `page_id`si).
 *   `create`   → `opId` + `params` platform operasyonuyla oluşturulur
 *                (`PlatformOpsService`, ADMIN), dönen kimlik `leadFormRef` olur.
 */
export type BriefLeadFormPlan =
  | { mode: 'existing'; ref: string }
  | { mode: 'create'; opId: string; params: Record<string, unknown> };

function leadFormField(platform: AdPlatform, goal: CampaignGoal, options: Readonly<Record<string, unknown>>) {
  if (goal !== 'leads') return null;
  if (platform === AdPlatform.META) {
    return metaLeadMode(options) === 'instantForm' ? BRIEF_GAP.META_LEAD_FORM : null;
  }
  if (platform === AdPlatform.TIKTOK) return BRIEF_GAP.TIKTOK_LEAD_FORM;
  return platform === AdPlatform.LINKEDIN ? BRIEF_GAP.LINKEDIN_LEAD_FORM : null;
}

function leadFormPlanFor(
  brief: CampaignBrief,
  platform: AdPlatform,
  options: Readonly<Record<string, unknown>>,
  identity: string | null,
): BriefLeadFormPlan | null {
  const field = leadFormField(platform, brief.goal, options);
  /* TikTok: yalnız MEVCUT form (düz `page_id` dizesi) — API form kurmuyor,
     `create` biçimi burada hiç yorumlanmıyor (gap denetimi zaten reddetti). */
  if (platform === AdPlatform.TIKTOK) {
    const id = field ? optionText(options, field) : null;
    return id ? { mode: 'existing', ref: id } : null;
  }
  const value = field ? parseLeadFormValue(options[field]) : null;
  if (!value) return null;

  if (value.mode === 'existing') {
    return { mode: 'existing', ref: platform === AdPlatform.LINKEDIN ? linkedInFormUrn(value.id) : value.id };
  }

  const questions = value.questions ?? BRIEF_LEAD_DEFAULT_QUESTIONS;
  /* Form adı kampanya adından — kullanıcının platform panelinde hangi
     kampanyanın formu olduğunu görebilmesi için. */
  const name = `${brief.name} — form`;

  if (platform === AdPlatform.META) {
    const title = value.headline ?? (brief.headline.length <= 60 ? brief.headline : undefined);
    return {
      mode: 'create',
      opId: 'meta.leadform.create',
      params: {
        pageId: identity ?? '',
        name,
        questions: questions.map((question) => ({ type: META_LEAD_QUESTION[question] })),
        privacyPolicy: { url: value.privacyPolicyUrl },
        /* Teşekkür ekranı YALNIZ metni verildiyse: verilmezse Meta'nın kendi
           varsayılan ekranı — uydurma bir metin yazmıyoruz. */
        ...(value.thankYouMessage && title
          ? {
              thankYouPage: {
                title,
                body: value.thankYouMessage,
                buttonType: 'VIEW_WEBSITE',
                websiteUrl: brief.linkUrl,
              },
            }
          : {}),
      },
    };
  }

  const language = (optionText(options, BRIEF_GAP.LINKEDIN_LANGUAGE) ?? '').toLowerCase();
  const labelIndex = language === 'tr' ? 2 : 1;
  return {
    mode: 'create',
    opId: 'linkedin.leadform.create',
    params: {
      name,
      locale: { country: brief.audience.countries[0] ?? '', language },
      headline: value.headline ?? brief.headline,
      description: value.description ?? brief.bodyText,
      questions: questions.flatMap((question) =>
        LINKEDIN_LEAD_QUESTION[question].map((row) => ({
          question: row[labelIndex] ?? row[1],
          predefinedField: row[0],
          required: true,
        })),
      ),
      privacyPolicyUrl: value.privacyPolicyUrl,
      thankYou: {
        message: value.thankYouMessage ?? LINKEDIN_THANK_YOU[language] ?? LINKEDIN_THANK_YOU.en,
        /* Teşekkür sayfasının "siteye git" bağlantısı yalnız https adreste —
           LinkedIn şeması başka şema kabul etmiyor. */
        ...(brief.linkUrl.startsWith('https://')
          ? { landingPage: brief.linkUrl, ctaLabel: 'VISIT_COMPANY_WEBSITE' }
          : {}),
      },
    },
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Eksik alan listesi
// ═══════════════════════════════════════════════════════════════════════════

/** `briefGaps`in brief'e bağlı alanlar için okuduğu kısım. */
export type BriefGapContext = Pick<CampaignBrief, 'audience' | 'headline' | 'bodyText'> & {
  /// 30 Eyl 2026 — uygulama amacında mağaza adresinden kimlik türetmek için.
  linkUrl?: string;
};

function choice(labelPrefix: string, value: string): BriefGapChoice {
  return { value, labelKey: `campaignBrief.choice.${labelPrefix}.${value}` };
}

function ageGap(platform: AdPlatform, audience: BriefAudience): BriefGap | null {
  const plan = agePlan(platform, audience);
  const field = AGE_GAP_FIELD[platform];
  if (plan.kind !== 'choose' || !field) return null;
  return {
    field,
    platform,
    kind: 'choice',
    min: 1,
    labelKey: 'campaignBrief.gap.ageRange.label',
    hintKey: `campaignBrief.gap.ageRange.hint.${platform}`,
    choices: plan.choices.map((row, index) => ({
      value: row.value,
      /* İlk seçenek içte kalan (dar), ikincisi kapsayan (geniş) — tek seçenek
         varsa hangisi olduğunu aralığın kendisi söylüyor. */
      labelKey:
        plan.choices.length === 1
          ? 'campaignBrief.gap.ageRange.only'
          : index === 0
            ? 'campaignBrief.gap.ageRange.narrower'
            : 'campaignBrief.gap.ageRange.wider',
      params: { range: spanLabel(row.span) },
    })),
  };
}

/**
 * Bir platformun bu brief için isteyeceği eksik alanlar.
 *
 * ⚠️ Fonksiyon SAF: aynı girdiye aynı listeyi veriyor ve hiçbir yere bakmıyor.
 * Kullanıcının o alanları doldurup doldurmadığı ayrı bir soru
 * (`compileBrief()`), çünkü plan ucu "ne isteyeceğim"i, yayınlama ucu "ne
 * eksik kaldı"yı soruyor.
 *
 * `brief` (29 Eyl 2026, Faz 5) İSTEĞE BAĞLI: verilirse brief'in İÇERİĞİNE bağlı
 * alanlar da ekleniyor — yaş aralığı platformun kovasına oturmuyorsa seçim,
 * metin platforma sığmıyorsa kısa metin. Verilmezse yalnız amaç × platform.
 */
export function briefGaps(goal: CampaignGoal, platform: AdPlatform, brief?: BriefGapContext): BriefGap[] {
  const gaps: BriefGap[] = [];
  const optimization = briefOptimization(goal, platform);
  const age = brief ? ageGap(platform, brief.audience) : null;

  if (platform === AdPlatform.META) {
    gaps.push({
      field: BRIEF_GAP.META_IDENTITY,
      platform,
      kind: 'identity',
      min: 1,
      labelKey: 'campaignBrief.gap.meta.identityRef.label',
      hintKey: 'campaignBrief.gap.meta.identityRef.hint',
    });

    const pixel: BriefGap = {
      field: BRIEF_GAP.META_PIXEL,
      platform,
      kind: 'text',
      min: 1,
      labelKey: 'campaignBrief.gap.meta.pixelId.label',
      hintKey: 'campaignBrief.gap.meta.pixelId.hint',
    };

    if (goal === 'leads') {
      /* 🔴 Varsayılan ANINDA FORM: piksel yok, form var. "Web sitesi formu"
         seçilirse piksel (LEAD olayı) sorulur, form sorulmaz. */
      gaps.push(
        {
          field: BRIEF_GAP.META_LEAD_MODE,
          platform,
          kind: 'choice',
          min: 0,
          labelKey: 'campaignBrief.gap.meta.leadMode.label',
          hintKey: 'campaignBrief.gap.meta.leadMode.hint',
          choices: META_LEAD_MODES.map((mode) => choice('metaLeadMode', mode)),
          defaultValue: 'instantForm',
        },
        {
          field: BRIEF_GAP.META_LEAD_FORM,
          platform,
          kind: 'leadForm',
          min: 1,
          labelKey: 'campaignBrief.gap.meta.leadForm.label',
          hintKey: 'campaignBrief.gap.meta.leadForm.hint',
          source: {
            opId: 'meta.leadform.list',
            params: {},
            paramsFromGaps: { pageId: BRIEF_GAP.META_IDENTITY },
            listPath: 'forms',
            valueField: 'id',
            labelField: 'name',
          },
          leadForm: {
            createOpId: 'meta.leadform.create',
            createRequiresAdmin: 'withThankYouMessage',
            questionChoices: BRIEF_LEAD_QUESTIONS,
            defaultQuestions: BRIEF_LEAD_DEFAULT_QUESTIONS,
            httpsPrivacyPolicy: false,
            needsLanguage: false,
          },
          when: { field: BRIEF_GAP.META_LEAD_MODE, equals: 'instantForm' },
        },
        { ...pixel, when: { field: BRIEF_GAP.META_LEAD_MODE, equals: 'website' } },
      );
    } else if (optimization?.requiresPromotedObject) {
      /* 🔴 Piksel kararı KATALOGDAN okunuyor — bkz. `BRIEF_GAP.META_PIXEL`. */
      gaps.push(pixel);
    }

    /* Kitle ayrıntısı (30 Eyl 2026) — hepsi İSTEĞE BAĞLI. Boş bırakılırsa
       bugünkü gibi yalnız ülke/yaş/cinsiyet gidiyor. */
    gaps.push(
      {
        field: BRIEF_GAP.META_INTERESTS,
        platform,
        kind: 'search',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.meta.interests.label',
        hintKey: 'campaignBrief.gap.meta.interests.hint',
        source: {
          opId: 'meta.targeting.search',
          params: { type: 'adinterest', limit: 25 },
          queryParam: 'query',
          minQuery: 2,
          listPath: 'results',
          valueField: 'id',
          labelField: 'name',
          detailField: 'topic',
        },
      },
      {
        field: BRIEF_GAP.META_AUDIENCES,
        platform,
        kind: 'source',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.meta.audiences.label',
        hintKey: 'campaignBrief.gap.meta.audiences.hint',
        source: {
          opId: 'meta.audience.list',
          params: { limit: 200 },
          listPath: 'audiences',
          valueField: 'id',
          labelField: 'name',
          detailField: 'subtype',
          keep: { subtype: 'subtype' },
        },
      },
      {
        field: BRIEF_GAP.META_LOCATIONS,
        platform,
        kind: 'search',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.meta.locations.label',
        hintKey: 'campaignBrief.gap.meta.locations.hint',
        source: {
          opId: 'meta.targeting.search',
          params: { type: 'adgeolocation', locationTypes: ['city', 'region'], limit: 25 },
          queryParam: 'query',
          minQuery: 2,
          listPath: 'results',
          valueField: 'key',
          labelField: 'name',
          detailField: 'country_name',
          /* 🔴 Tür ŞART: Meta şehri `cities`, bölgeyi `regions` altında
             istiyor; ülke kodu da kapsanan ülkeyi `countries`ten düşürmek
             için (Meta çakışan konumu reddediyor). */
          keep: { type: 'type', countryCode: 'country_code' },
        },
      },
    );
  }

  if (platform === AdPlatform.GOOGLE) {
    /* 30 Eyl 2026: alanlar Google TÜRÜNE göre (`google.campaignType`). Amacın
       tek türü varsa (uygulama) koşul yazılmıyor — alan her zaman geçerli;
       türlerin hiçbiri alanı istemiyorsa alan hiç eklenmiyor. Arama (bugünkü
       varsayılan) seçiliyken liste ESKİSİYLE AYNI: kelime + başlık + açıklama. */
    const types = GOOGLE_TYPES_BY_GOAL[goal];
    const pushFor = (list: readonly GoogleBriefCampaignType[], gap: BriefGap): void => {
      const hit = types.filter((type) => list.includes(type));
      if (hit.length === 0) return;
      gaps.push(
        hit.length === types.length ? gap : { ...gap, when: { field: BRIEF_GAP.GOOGLE_CAMPAIGN_TYPE, in: hit } },
      );
    };
    const withImages: readonly GoogleBriefCampaignType[] = ['PERFORMANCE_MAX', 'DEMAND_GEN', 'YOUTUBE'];
    const withText: readonly GoogleBriefCampaignType[] = ['SEARCH', 'PERFORMANCE_MAX', 'DEMAND_GEN', 'YOUTUBE', 'APP'];
    const withAudiences: readonly GoogleBriefCampaignType[] = ['SEARCH', 'DEMAND_GEN', 'YOUTUBE'];

    if (types.length > 1) {
      gaps.push({
        field: BRIEF_GAP.GOOGLE_CAMPAIGN_TYPE,
        platform,
        kind: 'choice',
        min: 0,
        labelKey: 'campaignBrief.gap.google.campaignType.label',
        hintKey: 'campaignBrief.gap.google.campaignType.hint',
        choices: types.map((value) => choice('googleCampaignType', value)),
        defaultValue: types[0],
      });
    }
    pushFor(['SEARCH'], {
      field: BRIEF_GAP.GOOGLE_KEYWORDS,
      platform,
      kind: 'list',
      min: GOOGLE_MIN_KEYWORDS,
      labelKey: 'campaignBrief.gap.google.keywords.label',
      hintKey: 'campaignBrief.gap.google.keywords.hint',
    });
    pushFor(withText, {
      field: BRIEF_GAP.GOOGLE_HEADLINES,
      platform,
      kind: 'list',
      min: GOOGLE_MIN_HEADLINES,
      labelKey: 'campaignBrief.gap.google.headlines.label',
      hintKey: 'campaignBrief.gap.google.headlines.hint',
    });
    pushFor(withText, {
      field: BRIEF_GAP.GOOGLE_DESCRIPTIONS,
      platform,
      kind: 'list',
      min: GOOGLE_MIN_DESCRIPTIONS,
      labelKey: 'campaignBrief.gap.google.descriptions.label',
      hintKey: 'campaignBrief.gap.google.descriptions.hint',
    });
    pushFor(['PERFORMANCE_MAX', 'YOUTUBE'], {
      field: BRIEF_GAP.GOOGLE_LONG_HEADLINES,
      platform,
      kind: 'list',
      min: 1,
      maxLength: GOOGLE_LONG_HEADLINE_MAX,
      labelKey: 'campaignBrief.gap.google.longHeadlines.label',
      hintKey: 'campaignBrief.gap.google.longHeadlines.hint',
    });
    pushFor(withImages, {
      field: BRIEF_GAP.GOOGLE_BUSINESS_NAME,
      platform,
      kind: 'text',
      min: 1,
      maxLength: GOOGLE_BUSINESS_NAME_MAX,
      labelKey: 'campaignBrief.gap.google.businessName.label',
      hintKey: 'campaignBrief.gap.google.businessName.hint',
    });
    pushFor(withImages, {
      field: BRIEF_GAP.GOOGLE_LOGO,
      platform,
      kind: 'source',
      min: 1,
      labelKey: 'campaignBrief.gap.google.logo.label',
      hintKey: 'campaignBrief.gap.google.logo.hint',
      source: {
        opId: 'google.asset.list',
        params: { type: 'IMAGE', limit: 200 },
        listPath: 'assets',
        valueField: 'asset.resource_name',
        labelField: 'asset.name',
      },
    });
    pushFor(['YOUTUBE'], {
      field: BRIEF_GAP.GOOGLE_YOUTUBE_VIDEOS,
      platform,
      kind: 'source',
      min: 1,
      multiple: true,
      labelKey: 'campaignBrief.gap.google.youtubeVideos.label',
      hintKey: 'campaignBrief.gap.google.youtubeVideos.hint',
      source: {
        opId: 'google.asset.list',
        params: { type: 'YOUTUBE_VIDEO', limit: 200 },
        listPath: 'assets',
        valueField: 'asset.youtube_video_asset.youtube_video_id',
        labelField: 'asset.name',
      },
    });
    pushFor(['SHOPPING'], {
      field: BRIEF_GAP.GOOGLE_MERCHANT,
      platform,
      kind: 'source',
      min: 1,
      labelKey: 'campaignBrief.gap.google.merchantId.label',
      hintKey: 'campaignBrief.gap.google.merchantId.hint',
      source: {
        opId: 'google.merchant.list_links',
        params: {},
        listPath: 'links',
        valueField: 'product_link.merchant_center.merchant_center_id',
        labelField: 'product_link.merchant_center.merchant_center_id',
      },
    });
    if (types.includes('APP')) {
      /* Mağaza adresinden türetilebiliyorsa İSTEĞE BAĞLI (brief'in adresi
         zaten mağaza sayfası) — türetilemiyorsa sorulur. */
      const derived = brief?.linkUrl ? appFromStoreUrl(brief.linkUrl) : null;
      pushFor(['APP'], {
        field: BRIEF_GAP.GOOGLE_APP_ID,
        platform,
        kind: 'text',
        min: derived ? 0 : 1,
        maxLength: 200,
        labelKey: 'campaignBrief.gap.google.appId.label',
        hintKey: 'campaignBrief.gap.google.appId.hint',
      });
      pushFor(['APP'], {
        field: BRIEF_GAP.GOOGLE_APP_STORE,
        platform,
        kind: 'choice',
        min: derived ? 0 : 1,
        labelKey: 'campaignBrief.gap.google.appStore.label',
        hintKey: 'campaignBrief.gap.google.appStore.hint',
        choices: GOOGLE_APP_STORES.map((value) => choice('googleAppStore', value)),
        ...(derived ? { defaultValue: derived.store } : {}),
      });
    }

    /* Kitle ayrıntısı — İSTEĞE BAĞLI. Konum her türde (kampanya seviyesi);
       kitle/ilgi arama ve Demand Gen'de (Performance Max kitleyi "sinyal"
       olarak ayrı bir kaynakla alıyor — brief'te yok, bkz. `googleCatalogOp`). */
    gaps.push({
      field: BRIEF_GAP.GOOGLE_LOCATIONS,
      platform,
      kind: 'search',
      min: 0,
      multiple: true,
      labelKey: 'campaignBrief.gap.google.locations.label',
      hintKey: 'campaignBrief.gap.google.locations.hint',
      source: {
        opId: 'google.geo.suggest',
        params: { locale: 'en' },
        queryParam: 'query',
        minQuery: 2,
        listPath: 'suggestions',
        valueField: 'id',
        labelField: 'canonicalName',
        detailField: 'targetType',
        keep: { countryCode: 'countryCode', type: 'targetType' },
      },
    });
    pushFor(withAudiences, {
      field: BRIEF_GAP.GOOGLE_USER_LISTS,
      platform,
      kind: 'search',
      min: 0,
      multiple: true,
      labelKey: 'campaignBrief.gap.google.userLists.label',
      hintKey: 'campaignBrief.gap.google.userLists.hint',
      source: {
        opId: 'google.audience.search_segments',
        params: { kinds: ['USER_LIST'], limit: 50 },
        queryParam: 'query',
        minQuery: 2,
        listPath: 'userLists',
        valueField: 'user_list.resource_name',
        labelField: 'user_list.name',
        detailField: 'user_list.type',
      },
    });
    pushFor(withAudiences, {
      field: BRIEF_GAP.GOOGLE_INTERESTS,
      platform,
      kind: 'search',
      min: 0,
      multiple: true,
      labelKey: 'campaignBrief.gap.google.interests.label',
      hintKey: 'campaignBrief.gap.google.interests.hint',
      source: {
        opId: 'google.audience.search_segments',
        params: { kinds: ['AFFINITY', 'IN_MARKET'], limit: 50 },
        queryParam: 'query',
        minQuery: 2,
        listPath: 'userInterests',
        valueField: 'user_interest.user_interest_id',
        labelField: 'user_interest.name',
        detailField: 'user_interest.taxonomy_type',
      },
    });
  }

  if (platform === AdPlatform.LINKEDIN) {
    gaps.push(
      {
        field: BRIEF_GAP.LINKEDIN_IDENTITY,
        platform,
        kind: 'text',
        min: 1,
        labelKey: 'campaignBrief.gap.linkedin.identityRef.label',
        hintKey: 'campaignBrief.gap.linkedin.identityRef.hint',
      },
      {
        field: BRIEF_GAP.LINKEDIN_LANGUAGE,
        platform,
        kind: 'text',
        min: 0,
        maxLength: 2,
        labelKey: 'campaignBrief.gap.linkedin.language.label',
        hintKey: 'campaignBrief.gap.linkedin.language.hint',
      },
    );
    if (age) gaps.push(age);
    if (goal === 'leads') {
      gaps.push({
        field: BRIEF_GAP.LINKEDIN_LEAD_FORM,
        platform,
        kind: 'leadForm',
        min: 1,
        labelKey: 'campaignBrief.gap.linkedin.leadForm.label',
        hintKey: 'campaignBrief.gap.linkedin.leadForm.hint',
        source: {
          opId: 'linkedin.leadform.list',
          params: {},
          listPath: '',
          valueField: 'id',
          labelField: 'name',
        },
        leadForm: {
          createOpId: 'linkedin.leadform.create',
          createRequiresAdmin: 'always',
          questionChoices: BRIEF_LEAD_QUESTIONS,
          defaultQuestions: BRIEF_LEAD_DEFAULT_QUESTIONS,
          httpsPrivacyPolicy: true,
          textFields: LINKEDIN_LEAD_FORM_LIMITS,
          needsLanguage: true,
        },
      });
    }
  }

  if (platform === AdPlatform.TIKTOK) {
    gaps.push(
      {
        field: BRIEF_GAP.TIKTOK_IDENTITY,
        platform,
        kind: 'identity',
        min: 1,
        labelKey: 'campaignBrief.gap.tiktok.identityRef.label',
        hintKey: 'campaignBrief.gap.tiktok.identityRef.hint',
      },
      {
        field: BRIEF_GAP.TIKTOK_PLACEMENT,
        platform,
        kind: 'choice',
        min: 0,
        labelKey: 'campaignBrief.gap.tiktok.placement.label',
        hintKey: 'campaignBrief.gap.tiktok.placement.hint',
        choices: TIKTOK_BRIEF_PLACEMENTS.map((value) => choice('tiktokPlacement', value)),
        defaultValue: 'AUTOMATIC',
      },
    );
    if (goal === 'leads') {
      /* 🔴 Yalnız SEÇİM (`source`, `leadForm` değil): TikTok API'si Instant
         Form oluşturmuyor — yeni form Ads Manager'da, plan satırındaki not
         bunu söylüyor. Taslak (EDITED) form reklama bağlanamaz → yalnız
         yayımlanmışlar listelenir. */
      gaps.push({
        field: BRIEF_GAP.TIKTOK_LEAD_FORM,
        platform,
        kind: 'source',
        min: 1,
        labelKey: 'campaignBrief.gap.tiktok.leadForm.label',
        hintKey: 'campaignBrief.gap.tiktok.leadForm.hint',
        source: {
          opId: 'tiktok.leadform.list',
          params: { status: 'PUBLISHED' },
          listPath: 'forms',
          valueField: 'page_id',
          labelField: 'title',
        },
      });
    }
    if (optimization?.requiresPromotedObject) {
      gaps.push(
        {
          field: BRIEF_GAP.TIKTOK_PIXEL,
          platform,
          kind: 'source',
          min: 1,
          labelKey: 'campaignBrief.gap.tiktok.pixelId.label',
          hintKey: 'campaignBrief.gap.tiktok.pixelId.hint',
          source: {
            opId: 'tiktok.pixel.list',
            params: {},
            listPath: 'pixels',
            valueField: 'pixel_id',
            labelField: 'pixel_name',
          },
        },
        {
          field: BRIEF_GAP.TIKTOK_OPTIMIZATION_EVENT,
          platform,
          kind: 'choice',
          min: 0,
          labelKey: 'campaignBrief.gap.tiktok.optimizationEvent.label',
          hintKey: 'campaignBrief.gap.tiktok.optimizationEvent.hint',
          choices: TIKTOK_BRIEF_EVENTS.map((value) => choice('tiktokEvent', value)),
          ...(optimization.customEventType ? { defaultValue: optimization.customEventType } : {}),
        },
      );
    }
    if (age) gaps.push(age);
    if (brief && !tiktokTextFits(brief.bodyText)) {
      gaps.push({
        field: BRIEF_GAP.TIKTOK_AD_TEXT,
        platform,
        kind: 'text',
        min: 1,
        maxLength: TIKTOK_AD_TEXT_MAX,
        labelKey: 'campaignBrief.gap.tiktok.adText.label',
        hintKey: 'campaignBrief.gap.tiktok.adText.hint',
      });
    }
    /* Kitle ayrıntısı (30 Eyl 2026) — İSTEĞE BAĞLI. Adapter bu iki anahtarı
       zaten tanıyor (`tiktok.rmf.ts` → `ADSET_OPTIONS`: `audienceIds`,
       `interestCategoryIds`). 🔴 Liste alan adları TikTok'un HAM yanıtından
       (`unverifiedShape`) — depoda eşlenmiş değil; canlıda farklıysa liste boş
       gelir ve alan elle kimlik girişine düşer. */
    gaps.push(
      {
        field: BRIEF_GAP.TIKTOK_AUDIENCES,
        platform,
        kind: 'source',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.tiktok.audiences.label',
        hintKey: 'campaignBrief.gap.tiktok.audiences.hint',
        source: {
          opId: 'tiktok.audience.list',
          params: { kind: 'CUSTOM', page: 1 },
          listPath: 'list',
          valueField: 'audience_id',
          labelField: 'name',
          detailField: 'audience_type',
          unverifiedShape: true,
        },
      },
      {
        field: BRIEF_GAP.TIKTOK_INTERESTS,
        platform,
        kind: 'source',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.tiktok.interests.label',
        hintKey: 'campaignBrief.gap.tiktok.interests.hint',
        source: {
          opId: 'tiktok.targeting.search',
          params: { kind: 'INTEREST_CATEGORY' },
          listPath: 'interest_categories',
          valueField: 'id',
          labelField: 'name',
          unverifiedShape: true,
        },
      },
    );
  }

  if (platform === AdPlatform.TWITTER) {
    gaps.push(
      {
        field: BRIEF_GAP.X_FUNDING_INSTRUMENT,
        platform,
        kind: 'source',
        min: 0,
        labelKey: 'campaignBrief.gap.x.fundingInstrumentId.label',
        hintKey: 'campaignBrief.gap.x.fundingInstrumentId.hint',
        source: {
          opId: 'x.funding.list',
          params: {},
          listPath: 'fundingInstruments',
          valueField: 'id',
          labelField: 'description',
        },
      },
      {
        field: BRIEF_GAP.X_IDENTITY,
        platform,
        kind: 'source',
        min: 0,
        labelKey: 'campaignBrief.gap.x.identityRef.label',
        hintKey: 'campaignBrief.gap.x.identityRef.hint',
        source: {
          opId: 'x.promotable_users.list',
          params: {},
          listPath: 'users',
          valueField: 'userId',
          labelField: 'userId',
        },
      },
      {
        field: BRIEF_GAP.X_PLACEMENTS,
        platform,
        kind: 'choice',
        min: 0,
        multiple: true,
        labelKey: 'campaignBrief.gap.x.placements.label',
        hintKey: 'campaignBrief.gap.x.placements.hint',
        choices: X_BRIEF_PLACEMENTS.map((value) => choice('xPlacement', value)),
        defaultValue: ['ALL_ON_TWITTER'],
      },
    );
    if (optimization?.requiresPromotedObject) {
      gaps.push({
        field: BRIEF_GAP.X_WEB_EVENT_TAG,
        platform,
        kind: 'source',
        min: 1,
        labelKey: 'campaignBrief.gap.x.webEventTag.label',
        hintKey: `campaignBrief.gap.x.webEventTag.hint.${goal}`,
        source: {
          opId: 'x.conversion.tag.list',
          params: {},
          listPath: 'tags',
          valueField: 'id',
          labelField: 'name',
        },
      });
    }
    if (age) gaps.push(age);
    if (brief && brief.headline.trim().length > X_CARD_TITLE_MAX) {
      gaps.push({
        field: BRIEF_GAP.X_CARD_TITLE,
        platform,
        kind: 'text',
        min: 1,
        maxLength: X_CARD_TITLE_MAX,
        labelKey: 'campaignBrief.gap.x.cardTitle.label',
        hintKey: 'campaignBrief.gap.x.cardTitle.hint',
      });
    }
    if (brief && weightedTextLength(brief.bodyText.trim()) > X_POST_TEXT_MAX) {
      gaps.push({
        field: BRIEF_GAP.X_POST_TEXT,
        platform,
        kind: 'text',
        min: 1,
        maxLength: X_POST_TEXT_MAX,
        labelKey: 'campaignBrief.gap.x.postText.label',
        hintKey: 'campaignBrief.gap.x.postText.hint',
      });
    }
  }

  return gaps;
}

// ═══════════════════════════════════════════════════════════════════════════
// Cevap denetimi
// ═══════════════════════════════════════════════════════════════════════════

function isBlank(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'string' && value.trim().length === 0) ||
    (Array.isArray(value) && value.length === 0)
  );
}

/// Seçim cevabı: `[]` boş, `null` GEÇERSİZ (listede olmayan değer).
function choiceValues(
  field: BriefGapField,
  allowed: readonly string[],
  multiple: boolean,
  raw: unknown,
): string[] | null {
  if (isBlank(raw)) return [];
  const values: unknown[] = multiple ? (Array.isArray(raw) ? raw : [raw]) : [raw];
  if (!values.every((value): value is string => typeof value === 'string' && allowed.includes(value))) {
    return null;
  }
  const unique = [...new Set(values as string[])];
  /* X: `ALL_ON_TWITTER` diğer X yerleşimlerini KAPSIYOR; ikisi birden X'te ret. */
  if (
    field === BRIEF_GAP.X_PLACEMENTS &&
    unique.includes('ALL_ON_TWITTER') &&
    unique.some((value) => value.startsWith('TWITTER_'))
  ) {
    return null;
  }
  return unique;
}

function choiceAnswer(gap: BriefGap, raw: unknown): string[] | null {
  return choiceValues(
    gap.field,
    (gap.choices ?? []).map((row) => row.value),
    gap.multiple === true,
    raw,
  );
}

/// Bu gap'in cevabı `when` koşulunu sağlıyor mu (koşulsuz gap her zaman aktif).
function gapActive(gap: BriefGap, options: Readonly<Record<string, unknown>>, gaps: readonly BriefGap[]): boolean {
  if (!gap.when) return true;
  const raw = options[gap.when.field];
  const effective = isBlank(raw) ? gaps.find((other) => other.field === gap.when?.field)?.defaultValue : raw;
  if (gap.when.in) return typeof effective === 'string' && gap.when.in.includes(effective);
  return effective === gap.when.equals;
}

/// Metin cevabının alana özgü kuralı (platformun kendi sayımı).
function textValid(gap: BriefGap, text: string): boolean {
  switch (gap.field) {
    case BRIEF_GAP.TIKTOK_AD_TEXT:
      return tiktokTextFits(text);
    case BRIEF_GAP.X_POST_TEXT:
      return weightedTextLength(text) <= X_POST_TEXT_MAX;
    case BRIEF_GAP.LINKEDIN_LANGUAGE:
      return /^[a-z]{2}$/i.test(text);
    /* Bağlayıcı sayısal olmayan `page_id`yi zaten reddediyor
       (LEAD_FORM_REF_MALFORMED) — ama kampanya ve reklam seti AÇILDIKTAN sonra;
       burada yakalamak yetim kampanyayı önlüyor. */
    case BRIEF_GAP.TIKTOK_LEAD_FORM:
      return /^\d{1,25}$/.test(text);
    /* 30 Eyl 2026 — Google katalog türleri. Biçimi burada yakalamak, atomik
       isteğin platformda düşmesinden (hiçbir şey açılmaz ama kullanıcı "neden"
       diye sorar) ucuz. */
    case BRIEF_GAP.GOOGLE_APP_ID:
      return /^([A-Za-z][\w.]{1,199}|\d{5,15})$/.test(text);
    case BRIEF_GAP.GOOGLE_MERCHANT:
      return /^\d{1,20}$/.test(text);
    case BRIEF_GAP.GOOGLE_LOGO:
      return GOOGLE_ASSET_RE.test(text) || UUID_RE.test(text);
    case BRIEF_GAP.GOOGLE_BUSINESS_NAME:
      return text.length <= GOOGLE_BUSINESS_NAME_MAX;
    default:
      return gap.maxLength === undefined || text.length <= gap.maxLength;
  }
}

const GOOGLE_ASSET_RE = /^customers\/\d+\/assets\/\d+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Meta konum seçimi → Graph biçimi. Tür öğenin `extra.type`ından ya da
 * (model yazdıysa) kimliğin önekinden: `city:2344061`, `region:3847`.
 * Şehir/bölge dışı tür `null` (cevap geçersiz — ülke brief'in kendi alanı).
 */
export function metaGeoOf(item: BriefPickedItem): { bucket: 'cities' | 'regions'; key: string; countryCode: string | null } | null {
  const prefixed = /^(city|region):(\S+)$/i.exec(item.id);
  const type = (prefixed?.[1] ?? item.extra?.type ?? '').toLowerCase();
  const key = prefixed?.[2] ?? item.id;
  if (!/^\d{1,20}$/.test(key)) return null;
  const countryCode = item.extra?.countryCode?.toUpperCase() ?? null;
  if (type === 'city') return { bucket: 'cities', key, countryCode };
  if (type === 'region') return { bucket: 'regions', key, countryCode };
  return null;
}

/// Google konum kimliği (`geoTargetConstants/1012782` ya da `1012782`) → sayı.
function googleGeoId(item: BriefPickedItem): string | null {
  const match = /^(?:geoTargetConstants\/)?(\d{1,20})$/.exec(item.id);
  return match?.[1] ?? null;
}

/// Seçilen öğeler bu alanın platform biçimine uyuyor mu.
function pickedValid(gap: BriefGap, items: readonly BriefPickedItem[]): boolean {
  switch (gap.field) {
    case BRIEF_GAP.META_LOCATIONS:
      return items.every((item) => metaGeoOf(item) !== null);
    case BRIEF_GAP.META_INTERESTS:
    case BRIEF_GAP.META_AUDIENCES:
    case BRIEF_GAP.TIKTOK_AUDIENCES:
    case BRIEF_GAP.TIKTOK_INTERESTS:
      return items.every((item) => /^\d{1,25}$/.test(item.id));
    case BRIEF_GAP.GOOGLE_LOCATIONS:
      return items.every((item) => googleGeoId(item) !== null);
    case BRIEF_GAP.GOOGLE_INTERESTS:
      return items.every((item) => /^(customers\/\d+\/userInterests\/)?\d+$/.test(item.id));
    case BRIEF_GAP.GOOGLE_USER_LISTS:
      return items.every((item) => /^(customers\/\d+\/userLists\/)?\d+$/.test(item.id));
    case BRIEF_GAP.GOOGLE_YOUTUBE_VIDEOS:
      return items.every((item) => /^[A-Za-z0-9_-]{11}$/.test(item.id));
    default:
      return true;
  }
}

/**
 * İstemci aynası için dışa açık adlar (web `lib/brief-gaps.ts`). Aynı kural
 * iki yerde yazılınca ayrışıyordu — web TikTok form kimliğinin sayısal
 * olmasını bilmiyordu. Son söz yine sunucuda (`compileBrief` → `missing`).
 */
export { gapActive as briefGapActive, textValid as briefGapTextValid };

function gapSatisfied(
  gap: BriefGap,
  options: Readonly<Record<string, unknown>>,
  brief: CampaignBrief,
): boolean {
  const raw = options[gap.field];
  if (gap.kind === 'search' || (gap.kind === 'source' && gap.multiple)) {
    const items = briefPickedItems(raw);
    if (items === null) return false;
    if (items.length === 0) return gap.min === 0;
    return items.length >= gap.min && pickedValid(gap, items);
  }
  switch (gap.kind) {
    case 'list': {
      const list = optionList(options, gap.field);
      /* Öğe sınırı yalnız `maxLength` taşıyan listede (Google uzun başlık);
         eski listeler (kelime/başlık) bugünkü gibi adapter'da denetleniyor. */
      return list.length >= gap.min && (gap.maxLength === undefined || list.every((item) => item.length <= gap.maxLength!));
    }
    case 'choice': {
      const values = choiceAnswer(gap, raw);
      if (values === null) return false;
      return values.length > 0 || gap.min === 0;
    }
    case 'leadForm': {
      if (isBlank(raw)) return gap.min === 0;
      const value = parseLeadFormValue(raw);
      return value !== null && leadFormValueValid(gap.platform, value, brief);
    }
    default: {
      if (isBlank(raw)) return gap.min === 0;
      const text = optionText(options, gap.field);
      return text !== null && textValid(gap, text);
    }
  }
}

/**
 * Google RSA alt sınırları.
 *
 * Google en az 3 başlık ve 2 açıklama istiyor; kelime sınırı BİZİM kararımız —
 * tek kelimeyle açılan bir arama kampanyası teknik olarak çalışır ama neredeyse
 * hiç eşleşme bulmaz. Üç, "gerçekten düşünülmüş" ile "alanı geçmek için bir
 * şey yazdım" arasındaki en düşük çizgi.
 */
export const GOOGLE_MIN_HEADLINES = 3;
export const GOOGLE_MIN_DESCRIPTIONS = 2;
export const GOOGLE_MIN_KEYWORDS = 3;
/// Google bir ad group'a 20 başlık / 4 açıklama kabul ediyor; kelime tavanı
/// bizim: tek seferde yönetilebilir bir küme.
export const GOOGLE_MAX_HEADLINES = 15;
export const GOOGLE_MAX_DESCRIPTIONS = 4;
export const GOOGLE_MAX_KEYWORDS = 50;
/// `CriterionError.KEYWORD_TEXT_TOO_LONG` — `google.keywords.write.ts` ile
/// AYNI sayı; iki farklı sınır, aynı kavramda çelişki olurdu.
export const GOOGLE_MAX_KEYWORD_LENGTH = 80;
/// Katalog türleri (30 Eyl 2026) — `googleCampaignCreateSchema` sınırları.
export const GOOGLE_LONG_HEADLINE_MAX = 90;
export const GOOGLE_BUSINESS_NAME_MAX = 25;
export const GOOGLE_HEADLINE_MAX = 30;
export const GOOGLE_DESCRIPTION_MAX = 90;

// ═══════════════════════════════════════════════════════════════════════════
// Brief'in kendisi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kitle — `rmfTargetingSchema`nın taşıyabildiği KADARI.
 *
 * Üç platformun ortak kesişimi bu: coğrafya, yaş, cinsiyet. İlgi alanları,
 * özel kitleler, kelime temaları platform kimliği taşıyor ve normalize
 * edilemez (`rmfTargetingSchema` başlığı). Brief onları HİÇ sormuyor —
 * sorsaydı, girilen değerin yalnız bir platformda işe yaradığını da söylemek
 * zorunda kalırdı ve bu, kaçınmaya çalıştığımız "platform ayarları" ekranının
 * ta kendisi.
 */
export const briefAudienceSchema = z
  .object({
    /// ISO 3166-1 alfa-2. Boş bırakılabilir: "platform karar versin".
    countries: z
      .array(z.string().trim().length(2).toUpperCase())
      .max(25)
      .transform((list) => [...new Set(list)])
      .default([]),
    ageMin: z.number().int().min(13).max(65).optional(),
    ageMax: z.number().int().min(13).max(65).optional(),
    /// Boş dizi "hepsi" DEĞİL, "seçim yok" demek — derleyici alanı hiç
    /// göndermiyor ve platform kendi varsayılanını uyguluyor.
    genders: z
      .array(z.enum(['male', 'female']))
      .max(2)
      .transform((list) => [...new Set(list)])
      .default([]),
  })
  .refine(
    (v) => v.ageMin === undefined || v.ageMax === undefined || v.ageMin <= v.ageMax,
    'Yaş alt sınırı üst sınırdan büyük olamaz',
  );
export type BriefAudience = z.infer<typeof briefAudienceSchema>;

/**
 * TEK BRIEF — adım 1, 2 ve 3'ün tamamı.
 *
 * Platform bilgisi BİLEREK yok: platform son adımda seçiliyor ve seçim
 * `briefPlacementSchema`ya giriyor. Brief'in kendisi hangi platformda
 * yayınlanacağını bilmiyor ve bilmemeli — bilseydi, alanlarının bir kısmı
 * yalnız bazı platformlarda anlamlı olurdu ve bu dosyanın var oluş sebebi
 * ortadan kalkardı.
 */
export const campaignBriefSchema = z.object({
  /* ── Adım 1: ne tanıtıyorsun ────────────────────────────────────────── */
  /**
   * Katalogdaki ürün. İSTEĞE BAĞLI: mağazası bağlı olmayan kullanıcı
   * doğrudan adres yapıştırabiliyor.
   *
   * Doluysa sunucu `linkUrl`, ad ve kreatif önerilerini üründen türetiyor;
   * boşsa kullanıcı hepsini elle veriyor.
   */
  productId: z.uuid().optional(),
  /**
   * 🔴 ZORUNLU — `publishSchema.linkUrl` ile aynı gerekçe: reklamın hedef
   * adresi bir sunum detayı değil, var oluş amacı. Boş bırakıldığında Meta
   * adapter'ı boşluğu `https://mentorivo.com` ile dolduruyordu, yani reklam
   * kullanıcının müşterisine değil BİZE trafik gönderiyordu.
   */
  linkUrl: z.url().max(2000),

  /* ── Adım 2: ne olsun, kime, ne kadar ───────────────────────────────── */
  name: z.string().trim().min(1).max(160),
  goal: z.enum(CAMPAIGN_GOALS),
  /**
   * Günlük TOPLAM bütçe, hesabın ana biriminde (kuruş değil).
   *
   * Tavan `dailyBudgetSchema` ile aynı: farklı olsalardı brief'ten açılan bir
   * kampanya düzenleme ucunda reddedilirdi.
   *
   * Alt sınır YOK ve bu bilinçli — minimum günlük bütçe HESABA göre değişiyor
   * (Meta `min_daily_budget` döndürüyor). Şemaya sabit bir sayı yazmak, başka
   * para biriminde açılmış bir hesapta yalan söylerdi.
   */
  dailyBudgetTotal: z.number().positive().max(1_000_000),
  /// Kaç gün yayında kalacak. Verilmezse süresiz — `endTime` hiç gönderilmiyor.
  durationDays: z.number().int().min(1).max(90).optional(),
  audience: briefAudienceSchema,
  /**
   * 🔴 PLATFORMLAR ARASI OTOMATİK BÜTÇE KAYDIRMA — kullanıcının açık izni.
   *
   * Açıksa yayına alındıktan sonra bu brief'in açtığı kampanyaları kapsayan
   * bir `budget_redistribute` kuralı kuruluyor: günlük çalışır, ROAS'a göre
   * sıralar, alt dilimden alıp üst dilime verir ve TOPLAM BÜTÇEYİ SABİT TUTAR
   * (`automation.ts` → `budgetRedistributeAction` maddesi 5).
   *
   * Yeni bir motor DEĞİL: aksiyon, uygulayıcısı ve kapsam çözümü zaten var ve
   * platformdan bağımsız çalışıyor (kapsam bir kampanya kimliği listesi,
   * `applyBudget` hedefin platformuna kendisi gidiyor).
   *
   * VARSAYILAN KAPALI. Para hareket ettiren bir kuralın kullanıcı farkında
   * olmadan kurulması, bu projede onay kapısıyla korunan her şeyin ihlali
   * olurdu.
   */
  autoOptimizeBudget: z.boolean().default(false),

  /* ── Adım 3: reklam ─────────────────────────────────────────────────── */
  /**
   * Kütüphaneden seçilen kreatifler — her biri BİR REKLAM olacak.
   *
   * Tavan `WIZARD_MAX_CREATIVES` ile aynı sayı ve aynı gerekçe: her seçim
   * gerçek bir reklam açıyor ve kredi harcıyor.
   *
   * ⚠️ Google bu listeyi KULLANMIYOR (`BRIEF_NOTICE.GOOGLE_TEXT_ONLY`) ama
   * alan yine de zorunlu: kullanıcı yalnız Google seçse bile reklam metnini
   * bir kreatiften türetiyoruz ve Meta/LinkedIn eklendiğinde aynı brief
   * yeniden derlenebilmeli.
   */
  creativeIds: z.array(z.uuid()).min(1).max(10),
  /// Meta feed'de ~40 karakterden sonra kırpıyor; tavan yine de cömert.
  headline: z.string().trim().min(1).max(200),
  /// ~125 karakterden sonra "devamı" ile katlanıyor; ilk cümle taşımalı.
  bodyText: z.string().trim().min(1).max(1000),
  /// `CTA_TYPES` (campaign-wizard.ts) içinden. Serbest metin değil: yazım
  /// hatası platform reddiyle son adımda ortaya çıkardı.
  ctaType: z.string().trim().min(1).max(64).optional(),
});
export type CampaignBrief = z.infer<typeof campaignBriefSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Yerleşim — adım 4'ün çıktısı
// ═══════════════════════════════════════════════════════════════════════════

/**
 * "Bu platformda, bu hesapta, bu bütçeyle, bu eksikler doldurulmuş olarak."
 *
 * `options` anahtarları `BRIEF_GAP` alan kimlikleri — arayüz hangi girdiyi
 * hangi anahtara yazacağını `briefGaps()` listesinden okuyor ve alan
 * kimliğinin platform gövdesindeki YERİNİ bilmiyor. O eşleme tek yerde:
 * `compileBrief()`. Arayüze "`google.keywords` aslında `platformOptions.
 * keywords`a gider" bilgisini taşımak, sözleşmeyi iki yere bölmek olurdu.
 */
export const briefPlacementSchema = z.object({
  /// BİZİM `AdAccount.id`imiz. Platform buradan türetiliyor — istemcinin ayrıca
  /// platform göndermesi, iki alanın çelişme ihtimalini yaratırdı.
  adAccountId: z.uuid(),
  /// Bu platforma düşen günlük pay. `splitDailyBudget()` öneriyor, kullanıcı
  /// değiştirebiliyor.
  dailyBudget: z.number().positive().max(1_000_000),
  /**
   * Eksik alanların cevapları, `BRIEF_GAP` anahtarlarıyla.
   *
   * Şema gevşek (`unknown`) çünkü değerin biçimi alana göre değişiyor: kimlik
   * bir dize, kelimeler bir dizi. SIKI doğrulama `compileBrief()` içinde,
   * alanın kendi kuralıyla yapılıyor ve reddi BİZİM cümlemiz açıklıyor.
   */
  options: z.record(z.string(), z.unknown()).default({}),
});
export type BriefPlacement = z.infer<typeof briefPlacementSchema>;

/** `POST /rmf/brief/plan` — kuru çalışma, hiçbir şey yazmıyor. */
export const briefPlanSchema = z.object({
  brief: campaignBriefSchema,
  /**
   * Değerlendirilecek hesaplar. Boş bırakılırsa sunucu workspace'teki
   * kampanya açabilen TÜM hesapları değerlendiriyor — arayüz son adımda
   * listeyi bu şekilde dolduruyor.
   */
  adAccountIds: z.array(z.uuid()).max(20).default([]),
});
export type BriefPlanInput = z.infer<typeof briefPlanSchema>;

/**
 * `POST /rmf/brief/launch` — gerçek yazma.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 BU UÇ YAYINA ALMIYOR — ve `activate` diye bir alanı YOK
 * ═══════════════════════════════════════════════════════════════════════════
 * Oluşturulan her şey PAUSED doğuyor (`rmf.ts` sözleşmesi) ve öyle kalıyor.
 * Yayına alma AYRI bir çağrı: `POST /campaigns/status`, ayrı bir
 * `Idempotency-Key`, ayrı bir denetim satırı.
 *
 * Bir `activate: true` bayrağı eklemek üç şeyi birden bozardı:
 *
 *   · Para harcatan tek çağrı, "oluştur"un içine gizlenmiş olurdu — bu
 *     projede onay kapısıyla korunan her şeyin ihlali.
 *   · Kısmi arıza okunamaz hâle gelirdi: üç platformun ikisi açıldı, biri
 *     düştü ve "yayına al" hangi hedefleri kapsadı sorusunun cevabı yanıtın
 *     içinde kaybolurdu.
 *   · `POST /campaigns/status` hedef DİZİSİ alıyor ve çoklu hedefte kısmi
 *     başarıyı zaten doğru raporluyor (`BulkActionResult`). İkinci bir
 *     yayına alma yolu yazmak, o davranışın ikinci bir kopyası demekti.
 *
 * Arayüz özet ekranında defteri gösteriyor ve yayına almayı AYRI bir düğmeyle
 * soruyor.
 */
export const briefLaunchSchema = z
  .object({
    brief: campaignBriefSchema,
    placements: z.array(briefPlacementSchema).min(1).max(10),
  })
  .superRefine((value, ctx) => {
    /* Aynı hesaba iki yerleşim: ikinci çağrı aynı hesapta İKİNCİ bir kampanya
       açardı ve kullanıcı bunu istemedi — bir kez seçti. */
    const seen = new Set<string>();
    for (const placement of value.placements) {
      if (seen.has(placement.adAccountId)) {
        ctx.addIssue({
          code: 'custom',
          path: ['placements'],
          message: 'Aynı reklam hesabı iki kez seçilemez',
        });
      }
      seen.add(placement.adAccountId);
    }

    /* 🔴 Payların toplamı girilen toplamı AŞAMAZ. Kullanıcı satırları elle
       değiştirebiliyor ve üç satırı da yukarı çekerek farkında olmadan
       toplamın iki katını harcatabilirdi. Aşağıda kalması SERBEST: eksik
       dağıtmak meşru bir seçim, fazla harcamak değil.

       Karşılaştırma kuruşta: 166,66 + 166,67 + 166,67 ondalıkta 500,0000001
       verebiliyor ve o, meşru bir bölümü reddederdi. */
    const spread = value.placements.reduce((sum, p) => sum + Math.round(p.dailyBudget * 100), 0);
    if (spread > Math.round(value.brief.dailyBudgetTotal * 100)) {
      ctx.addIssue({
        code: 'custom',
        path: ['placements'],
        message: 'Platform bütçelerinin toplamı günlük toplam bütçeyi aşıyor',
      });
    }
  });
export type BriefLaunchInput = z.infer<typeof briefLaunchSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Derleyici — brief × platform → rmf gövdeleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 REKLAM AÇMANIN YOLU PLATFORMA GÖRE DEĞİŞİYOR — ve bu ölçüldü.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Google'da `POST /creative/:id/publish` ÇALIŞMIYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * `GoogleAdsConnector.publishCreative` bilerek FIRLATIYOR ve yetenek bayrağı
 * da bunu söylüyor (`canPublishCreative: false`). Google'da reklam bağımsız
 * bir kreatif nesnesi değil; metinler `ad_group_ad`ın içinde yaşıyor ve tek
 * giriş `POST /rmf/ads`.
 *
 * Meta ve LinkedIn'de ise tersi doğru: `/creative/:id/publish` imzalamayı,
 * `Ad.creativeId` bağını ve kredi muhasebesini yapıyor; `POST /rmf/ads` ise
 * `assetUrl` taşımıyor (`rmfAdCreativeSchema` başlığı: "creative alanı yalnız
 * METİN taşıyor") ve oradan açılan Meta reklamı görselsiz olurdu.
 *
 * Yani her platform KENDİ doğrulanmış yolundan gidiyor. Tek yol seçip hepsine
 * uygulamak, iki platformdan birinde çalışmayan bir akış demekti.
 */
export type BriefAdPath =
  | {
      via: 'creative-publish';
      /// Her kimlik AYRI bir reklam. Sıra anlamlı değil.
      creativeIds: string[];
      /// `targetId` ad set oluşturulduktan sonra doluyor.
      publish: Omit<PublishInput, 'targetId' | 'targetLevel'>;
    }
  | {
      via: 'rmf-ads';
      name: string;
      creative: RmfAdCreative;
      /// RSA çoklu metni. Kelimeler BURADA DEĞİL — onlar ad group'un alanı.
      platformOptions: Record<string, unknown>;
    };

/** Derlenmiş yerleşim — üç çağrının gövdeleri, kimlikler hariç. */
export interface CompiledPlacement {
  adAccountId: string;
  platform: AdPlatform;
  objective: string;
  dailyBudget: number;
  campaign: CreateCampaignRequest;
  /// `campaignId` bir önceki çağrının sonucundan doluyor.
  adSet: Omit<CreateAdSetRequest, 'campaignId'>;
  ads: BriefAdPath;
  /**
   * Lead formu (29 Eyl 2026, Faz 5) — yalnız "potansiyel müşteri" amacında
   * Meta (anında form), LinkedIn ve TikTok'ta (Instant Form, yalnız
   * `existing`). `existing` ise `ads.publish.leadFormRef`
   * zaten dolu; `create` ise yayınlayıcı formu KAMPANYADAN ÖNCE oluşturup
   * dönen kimliği reklam adımına koyuyor (form kurulamazsa hiçbir şey açılmaz).
   */
  leadForm: BriefLeadFormPlan | null;
  /**
   * 🔴 KATALOG YOLU (30 Eyl 2026) — Google arama DIŞI türler. Doluysa
   * yayınlayıcı RMF üçlüsünü (kampanya → ad set → reklam) KOŞMUYOR; tek
   * `PlatformOpsService.run(opId, params)` çağrısı kampanyayı çocuklarıyla
   * açıyor (kampanya PAUSED). `campaign`/`adSet`/`ads` o durumda yalnız özet.
   */
  catalog?: { opId: string; params: Record<string, unknown> } | null;
}

/** Google arama dışı türlerin katalog operasyonu. */
export const GOOGLE_CAMPAIGN_CREATE_OP = 'google.campaign.create';

export type CompileResult =
  | { ok: true; placement: CompiledPlacement }
  | {
      ok: false;
      /// Cevaplanmamış `BRIEF_GAP` alanları.
      missing: BriefGapField[];
      /// Alan eksikliği değil de yapısal bir engelse (amaç desteklenmiyor).
      blocked: BriefNoticeCode | null;
    };

/** `placement.options` içinden dize oku. */
function optionText(options: Record<string, unknown>, field: BriefGapField): string | null {
  const value = options[field];
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return text.length > 0 ? text : null;
}

/**
 * `placement.options` içinden liste oku.
 *
 * Tek bir dize de kabul ediliyor ve tek elemanlı listeye çevriliyor: arayüz
 * bir alanı sehven çoklu yerine tekil gönderirse, kullanıcının yazdığı değer
 * SESSİZCE düşmesin. Boşlar ve tekrarlar atılıyor — aynı kelimeyi iki kez
 * yazmanın anlamı yok ve Google `DUPLICATE_KEYWORD` veriyor.
 */
function optionList(options: Record<string, unknown>, field: BriefGapField): string[] {
  const raw = options[field];
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
  const clean = list
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter((item) => item.length > 0);
  return [...new Set(clean)];
}

/**
 * BRIEF'İ BİR PLATFORMA DERLE.
 *
 * Saf fonksiyon: aynı girdiye aynı çıktıyı veriyor, hiçbir yere bakmıyor,
 * hiçbir şey yazmıyor. Hem `plan` ucu (kuru çalışma) hem `launch` ucu aynı
 * fonksiyonu çağırıyor — iki ayrı derleme, kullanıcının önizlediğinden başka
 * bir kampanyanın açılması demekti.
 *
 * `now` DIŞARIDAN geliyor: `durationDays` → `endTime` hesabı bir saate bağlı
 * ve fonksiyonun test edilebilir kalması, o saatin parametre olmasını
 * gerektiriyor.
 */
export function compileBrief(
  brief: CampaignBrief,
  placement: BriefPlacement,
  platform: AdPlatform,
  now: Date,
  context: BriefContext = {},
): CompileResult {
  const objective = objectiveFor(brief.goal, platform);
  if (objective === null) {
    return { ok: false, missing: [], blocked: BRIEF_NOTICE.GOAL_UNSUPPORTED };
  }

  /* Yapısal engeller — cevapla çözülemeyen, brief'in kendisi değişmeli
     (ülke yok, yaş hiçbir kovaya düşmüyor, TikTok adında emoji). */
  const structural = structuralBlock(brief, platform);
  if (structural) return { ok: false, missing: [], blocked: structural };

  /* Eksik alan denetimi — hangi alanın ne kadar değer istediğini `briefGaps()`
     söylüyor, burada tekrar yazılmıyor. `when` koşulu tutmayan alan sorulmaz;
     İSTEĞE BAĞLI alan (min 0) boş geçilebilir ama verilmişse doğrulanır. */
  const options = placement.options;
  const gaps = briefGaps(brief.goal, platform, brief);
  const missing: BriefGapField[] = [];
  for (const gap of gaps) {
    if (!gapActive(gap, options, gaps)) continue;
    if (!gapSatisfied(gap, options, brief)) missing.push(gap.field);
  }
  /* Koşullu zorunluluk: LinkedIn'de YENİ form, kampanyanın diliyle aynı dilde
     kurulmalı → dil boş geçilemez. */
  if (
    platform === AdPlatform.LINKEDIN &&
    parseLeadFormValue(options[BRIEF_GAP.LINKEDIN_LEAD_FORM])?.mode === 'create' &&
    optionText(options, BRIEF_GAP.LINKEDIN_LANGUAGE) === null &&
    !missing.includes(BRIEF_GAP.LINKEDIN_LANGUAGE)
  ) {
    missing.push(BRIEF_GAP.LINKEDIN_LANGUAGE);
  }
  /* Uygulama (30 Eyl 2026): kimlik/mağaza cevap ya da mağaza adresinden —
     ikisi de yoksa sorulur (gap `min: 0` iken bile). */
  const googleType = platform === AdPlatform.GOOGLE ? googleCampaignType(brief.goal, options) : null;
  if (googleType === 'APP' && !resolvedGoogleApp(brief, options)) {
    for (const field of [BRIEF_GAP.GOOGLE_APP_ID, BRIEF_GAP.GOOGLE_APP_STORE] as const) {
      if (!missing.includes(field)) missing.push(field);
    }
  }
  if (missing.length > 0) return { ok: false, missing, blocked: null };

  /* Medya — yerleşim cevabına bağlı olduğu için eksik alandan SONRA
     (TikTok'ta görsel yalnız Pangle'da geçer). */
  if (unsupportedMedia(brief, platform, options, context).length > 0) {
    return { ok: false, missing: [], blocked: BRIEF_NOTICE.MEDIA_UNSUPPORTED };
  }

  const budgetLevel = BUDGET_LEVEL[platform] ?? 'CAMPAIGN';
  const endTimeLevel = END_TIME_LEVEL[platform] ?? 'ADSET';
  const endTime =
    brief.durationDays !== undefined
      ? new Date(now.getTime() + brief.durationDays * 86_400_000).toISOString()
      : undefined;
  const identity = identityAnswer(platform, options);

  /* ── Google arama DIŞI türler — katalog operasyonu (30 Eyl 2026) ─────────
     RMF üçlüsü bu türlerde çalışmıyor (`GOOGLE_BRIEF_CAMPAIGN_TYPES`
     başlığı); kampanya ve çocukları TEK atomik istekte. */
  const catalogType = googleType ? googleCatalogTypeOf(googleType) : null;
  if (googleType && catalogType) {
    const built = googleCatalogOp(brief, placement, googleType, catalogType, endTime, context);
    if (!built.ok) return { ok: false, missing: built.missing, blocked: built.blocked };
    return {
      ok: true,
      placement: {
        adAccountId: placement.adAccountId,
        platform,
        objective: catalogType,
        dailyBudget: placement.dailyBudget,
        /* 🔴 Aşağıdaki üç gövde katalog yerleşiminde YÜRÜTÜLMÜYOR — yalnız
           kimin, hangi adla, hangi türde açılacağının özeti (defter/kart
           okuyor). Yayınlayıcı `catalog`u koşuyor. */
        campaign: {
          adAccountId: placement.adAccountId,
          name: brief.name,
          objective: catalogType,
          dailyBudget: placement.dailyBudget,
          ...(endTime ? { endTime } : {}),
        },
        adSet: { name: brief.name },
        ads: { via: 'rmf-ads', name: brief.name, creative: { linkUrl: brief.linkUrl }, platformOptions: {} },
        leadForm: null,
        catalog: { opId: GOOGLE_CAMPAIGN_CREATE_OP, params: built.params },
      },
    };
  }

  /* ── Kampanya ─────────────────────────────────────────────────────────── */
  const fundingInstrument =
    platform === AdPlatform.TWITTER ? optionText(options, BRIEF_GAP.X_FUNDING_INSTRUMENT) : null;
  const campaign: CreateCampaignRequest = {
    adAccountId: placement.adAccountId,
    name: brief.name,
    objective,
    ...(budgetLevel === 'CAMPAIGN' ? { dailyBudget: placement.dailyBudget } : {}),
    /* Boş dizi de GÖNDERİLİYOR: "hiçbiri" bir seçim ve Meta bağlayıcısı alanı
       boş olsa bile bekliyor. Brief bu soruyu HİÇ sormuyor — özel reklam
       kategorisi beyan etmek yanlış bildirildiğinde hesabı kapattırabiliyor ve
       "kredi/istihdam/konut/siyaset reklamı mı veriyorsunuz" sorusu, dört
       adımlık bir akışın sorabileceği bir soru değil. Beyan gerektiren
       kullanıcı gelişmiş akışı kullanır. (TikTok/X boş diziyi "yok" okuyor.) */
    specialAdCategories: [],
    ...(endTime && endTimeLevel === 'CAMPAIGN' ? { endTime } : {}),
    /* X: fon kaynağı KAMPANYANIN alanı. Verilmezse adapter hesabın tek aktif
       kaynağını seçiyor; birden çoksa açık bir hatayla reddediyor. */
    ...(fundingInstrument ? { platformOptions: { fundingInstrumentId: fundingInstrument } } : {}),
  };

  /* ── Ad set ───────────────────────────────────────────────────────────── */
  /* 🔴 Optimizasyon KATALOGDAN, elle değil — ve AMACA göre (`GOAL_OPTIMIZATION`).
     Meta/TikTok/X'te bu iki alan ZORUNLU, Google'da gönderilirse
     `FIELD_NOT_SUPPORTED_ON_PLATFORM` ile reddediliyor. Katalog Google'da boş
     dizi döndürüyor, yani "gönderme" kararı da oradan geliyor. */
  const optimization = briefOptimization(brief.goal, platform, options);

  /* Google'da seçilen şehir/bölgenin ÜLKESİ kampanyadan düşüyor: Google konum
     kriterlerini birleştiriyor (OR) — "Türkiye + İstanbul" tüm Türkiye demek,
     kullanıcının daralttığı şey sessizce genişlerdi (30 Eyl 2026). */
  const countries =
    platform === AdPlatform.GOOGLE
      ? uncoveredCountries(brief.audience.countries, googleLocationsOf(options))
      : brief.audience.countries;
  const genders = brief.audience.genders;
  /* Yaş: kovalı platformda (TikTok, LinkedIn, X) kova sınırlarına oturan
     aralık — brief'inki ya da kullanıcının seçtiği. `gapSatisfied` geçersiz
     cevabı zaten eledi; `null` burada gelmez. */
  const age = resolvedAge(platform, brief.audience, options) ?? {};
  const hasTargeting =
    countries.length > 0 || genders.length > 0 || age.ageMin !== undefined || age.ageMax !== undefined;

  const keywords = optionList(options, BRIEF_GAP.GOOGLE_KEYWORDS);
  const promotedObject = promotedObjectFor(platform, optimization, options, identity);
  const adSetOptions = adSetPlatformOptions(platform, optimization, options, keywords, countries);

  const adSet: Omit<CreateAdSetRequest, 'campaignId'> = {
    /* Ad set adı kampanya adından TÜRETİLİYOR, kullanıcıya sorulmuyor. İki
       ayrı ad istemek, kullanıcının kavramsal olarak tek gördüğü şeyi ikiye
       bölmek olurdu — bu akışta bir kampanyanın tek bir ad set'i var. */
    name: brief.name,
    ...(budgetLevel === 'ADSET' ? { dailyBudget: placement.dailyBudget } : {}),
    ...(optimization
      ? {
          optimizationGoal: optimization.optimizationGoal,
          billingEvent: optimization.billingEvent,
        }
      : {}),
    ...(promotedObject ? { promotedObject } : {}),
    ...(hasTargeting
      ? {
          targeting: {
            ...(countries.length > 0 ? { countries } : {}),
            ...age,
            ...(genders.length > 0 ? { genders } : {}),
          },
        }
      : {}),
    /* `startTime` HİÇ gönderilmiyor: varlık zaten PAUSED doğuyor, yani "hemen
       başla" harcama başlatmıyor ve tarih sormanın bir karşılığı yok. */
    ...(endTime && endTimeLevel === 'ADSET' ? { endTime } : {}),
    ...(adSetOptions ? { platformOptions: adSetOptions } : {}),
  };

  /* ── Lead formu ───────────────────────────────────────────────────────── */
  const leadForm = leadFormPlanFor(brief, platform, options, identity);

  /* ── Reklam ───────────────────────────────────────────────────────────── */
  const ads: BriefAdPath =
    platform === AdPlatform.GOOGLE
      ? {
          via: 'rmf-ads',
          name: brief.name,
          /* 🔴 YALNIZ hedef adres. Google RSA metni başlık/açıklama
             LİSTELERİNDEN geliyor (aşağıdaki platformOptions) ve adapter
             tekil `headline`/`bodyText`/`ctaType`i REDDEDİYOR
             (`google.create.ts`). 29 Eyl 2026'ya kadar bu üçü burada
             gönderiliyordu → her Google yerleşiminde reklam adımı düşüyor,
             kampanya reklamsız (sıfır gösterim) kalıyordu. */
          creative: {
            linkUrl: brief.linkUrl,
          },
          platformOptions: {
            [GOOGLE_RSA_OPTION_KEYS.headlines]: optionList(options, BRIEF_GAP.GOOGLE_HEADLINES),
            [GOOGLE_RSA_OPTION_KEYS.descriptions]: optionList(options, BRIEF_GAP.GOOGLE_DESCRIPTIONS),
          },
        }
      : {
          via: 'creative-publish',
          creativeIds: brief.creativeIds,
          publish: publishFieldsFor(platform, brief, options, identity, leadForm),
        };

  return {
    ok: true,
    placement: {
      adAccountId: placement.adAccountId,
      platform,
      objective,
      dailyBudget: placement.dailyBudget,
      campaign,
      adSet,
      ads,
      leadForm,
    },
  };
}

// ── Derleyicinin platform parçaları ──────────────────────────────────────

/// Platformun marka kimliği cevabı (Meta Sayfa, LinkedIn org, TikTok Spark, X kullanıcı).
const IDENTITY_FIELD: Readonly<Partial<Record<AdPlatform, BriefGapField>>> = {
  [AdPlatform.META]: BRIEF_GAP.META_IDENTITY,
  [AdPlatform.LINKEDIN]: BRIEF_GAP.LINKEDIN_IDENTITY,
  [AdPlatform.TIKTOK]: BRIEF_GAP.TIKTOK_IDENTITY,
  [AdPlatform.TWITTER]: BRIEF_GAP.X_IDENTITY,
};

function identityAnswer(platform: AdPlatform, options: Readonly<Record<string, unknown>>): string | null {
  const field = IDENTITY_FIELD[platform];
  return field ? optionText(options, field) : null;
}

/**
 * Brief'in kendisi değişmeden çözülemeyen engel — plan satırında ENGELLEYİCİ
 * uyarı, derlemede `blocked`. Sıra: ülke → ad → yaş.
 */
function structuralBlock(brief: CampaignBrief, platform: AdPlatform): BriefNoticeCode | null {
  if (wizardFieldRequirements(platform).countriesRequired && brief.audience.countries.length === 0) {
    return BRIEF_NOTICE.COUNTRIES_REQUIRED;
  }
  /* TikTok kampanya ve reklam seti adında emojiyi `40002` ile reddediyor
     (`assertNoEmoji`); brief adı ikisinin de adı. */
  if (platform === AdPlatform.TIKTOK && hasEmoji(brief.name)) return BRIEF_NOTICE.TEXT_EMOJI;
  if (agePlan(platform, brief.audience).kind === 'unsupported') return BRIEF_NOTICE.AGE_UNSUPPORTED;
  return null;
}

/// TikTok yerleşim cevabı (varsayılan otomatik).
function tiktokPlacement(options: Readonly<Record<string, unknown>>): TikTokBriefPlacement {
  const raw = options[BRIEF_GAP.TIKTOK_PLACEMENT];
  return (TIKTOK_BRIEF_PLACEMENTS as readonly unknown[]).includes(raw) ? (raw as TikTokBriefPlacement) : 'AUTOMATIC';
}

/**
 * Bu platformun bu yoldan KABUL ETMEDİĞİ kreatifler.
 *
 *   META     yalnız görsel — `publishCreative` videoyu reddediyor (video
 *            işleme eşzamansız; `meta.video.upload` + `meta.ad.create` yolu).
 *   TIKTOK   TikTok/otomatik yerleşimde yalnız video; Pangle'da görsel de.
 *   LINKEDIN, TWITTER  görsel ya da video.
 *   GOOGLE   kreatif kullanmıyor (`GOOGLE_TEXT_ONLY`).
 */
function unsupportedMedia(
  brief: CampaignBrief,
  platform: AdPlatform,
  options: Readonly<Record<string, unknown>>,
  context: BriefContext,
): string[] {
  const media = context.media;
  if (!media || platform === AdPlatform.GOOGLE) return [];
  const pangleOnly = platform === AdPlatform.TIKTOK && tiktokPlacement(options) === 'PANGLE';
  return brief.creativeIds.filter((id) => {
    const kind = media[id];
    if (kind === undefined) return false;
    if (platform === AdPlatform.META) return kind !== 'image';
    if (platform === AdPlatform.TIKTOK) return pangleOnly ? kind === 'other' : kind !== 'video';
    return kind === 'other';
  });
}

/** Reklam setinin hedef nesnesi (piksel/olay, sayfa, olay etiketi). */
function promotedObjectFor(
  platform: AdPlatform,
  optimization: OptimizationOption | null,
  options: Readonly<Record<string, unknown>>,
  identity: string | null,
): CreateAdSetRequest['promotedObject'] | null {
  switch (platform) {
    case AdPlatform.META: {
      /* 🔴 Piksel OLAYIYLA gidiyor (29 Eyl 2026). Olay katalogdan
         (`OptimizationOption.customEventType`): Meta `OFFSITE_CONVERSIONS` için
         `pixel_id` + `custom_event_type` ikisini birden istiyor; olaysız piksel
         ad set reddi ve içi boş kampanya demekti. */
      if (optimization?.requiresPromotedObject) {
        const pixelId = optionText(options, BRIEF_GAP.META_PIXEL);
        return pixelId
          ? { pixelId, ...(optimization.customEventType ? { customEventType: optimization.customEventType } : {}) }
          : null;
      }
      /* Anında form (ODAX `ON_AD`): hedef nesne SAYFA — piksel değil. */
      if (optimization?.destinationType === 'ON_AD' && identity) return { pageId: identity };
      return null;
    }
    case AdPlatform.LINKEDIN:
      /* 🔴 LinkedIn: şirket sayfası (`associatedEntity`) REKLAM SETİNİN alanı
         ve sponsorlu içerikte zorunlu. Adapter `pageId`yi
         `urn:li:organization:…`e çeviriyor ve biçimi doğruluyor. */
      return identity ? { pageId: identity } : null;
    case AdPlatform.TIKTOK: {
      /* TikTok `CONVERT`: `pixel_id` + `optimization_event` (adapter
         `customEventType`ı `optimization_event`e yazıyor). */
      const pixelId = optimization?.requiresPromotedObject ? optionText(options, BRIEF_GAP.TIKTOK_PIXEL) : null;
      return pixelId
        ? { pixelId, ...(optimization?.customEventType ? { customEventType: optimization.customEventType } : {}) }
        : null;
    }
    case AdPlatform.TWITTER: {
      /* X: dönüşüm = birincil web olay etiketi (`primary_web_event_tag`).
         🔴 `customEventType` GÖNDERİLMİYOR — adapter onu reddediyor; olay
         etiketin kendisi. */
      const tag = optimization?.requiresPromotedObject ? optionText(options, BRIEF_GAP.X_WEB_EVENT_TAG) : null;
      return tag ? { pixelId: tag } : null;
    }
    default:
      return null;
  }
}

/** Reklam setinin platforma özgü ekstraları (adapter izin listelerindeki anahtarlar). */
function adSetPlatformOptions(
  platform: AdPlatform,
  optimization: OptimizationOption | null,
  options: Readonly<Record<string, unknown>>,
  keywords: readonly string[],
  countries: readonly string[],
): Record<string, unknown> | null {
  switch (platform) {
    case AdPlatform.GOOGLE: {
      /* 🔴 KELİMELER AD GROUP'UN ALANI, reklamın değil. Google'da
         `ad_group_criterion` ad group'a bağlanıyor; reklamın `platformOptions`ına
         koymak, adapter'ın onları sessizce yok sayması demekti. */
      /* 30 Eyl 2026: konum (şehir/bölge — kampanya kriteri, ülkeyle AYNI
         yoldan) ve kitle (ad group'ta GÖZLEM — arama kampanyasında kitle
         erişimi daraltmıyor, teklif sinyali) — `google.create.ts` →
         `locationsFromOptions` / `audiencesFromOptions`. */
      const locations = googleLocationsOf(options).map((row) => `geoTargetConstants/${row.id}`);
      const userLists = (briefPickedItems(options[BRIEF_GAP.GOOGLE_USER_LISTS]) ?? []).map((item) => item.id);
      const userInterests = (briefPickedItems(options[BRIEF_GAP.GOOGLE_INTERESTS]) ?? []).map((item) =>
        item.id.replace(/^customers\/\d+\/userInterests\//, ''),
      );
      const out: Record<string, unknown> = {
        ...(keywords.length > 0 ? { keywords: [...keywords] } : {}),
        ...(locations.length > 0 ? { locations } : {}),
        ...(userLists.length > 0 || userInterests.length > 0
          ? {
              audiences: {
                ...(userLists.length > 0 ? { userLists } : {}),
                ...(userInterests.length > 0 ? { userInterests } : {}),
              },
            }
          : {}),
      };
      return Object.keys(out).length > 0 ? out : null;
    }
    case AdPlatform.META: {
      /* ODAX hedef yeri: anında form `ON_AD`. Meta adapter'ı `platformOptions`ı
         gövdeye AYNEN yazıyor (`destination_type` yasaklı anahtar değil). */
      /* 30 Eyl 2026: ilgi/kitle/şehir → `targeting_extras` (adapter hedeflemenin
         İÇİNE birleştiriyor; `targeting` anahtarı yasaklı — `rmf.ts`). */
      const extras = metaTargetingExtras(countries, options);
      const out: Record<string, unknown> = {
        ...(optimization?.destinationType ? { destination_type: optimization.destinationType } : {}),
        ...(extras ? { targeting_extras: extras } : {}),
      };
      return Object.keys(out).length > 0 ? out : null;
    }
    case AdPlatform.LINKEDIN: {
      /* Dil verildiyse reklam setinin `locale`i AÇIKÇA (çok dilli ülke, ya da
         yeni lead formuyla aynı dil). Verilmezse adapter tek dilli ülkeden
         türetiyor. */
      const language = optionText(options, BRIEF_GAP.LINKEDIN_LANGUAGE);
      const country = countries[0];
      return language && country ? { locale: { country, language: language.toLowerCase() } } : null;
    }
    case AdPlatform.TIKTOK: {
      const placement = tiktokPlacement(options);
      /* 30 Eyl 2026: özel/benzer kitle ve ilgi kategorisi — adapter'ın mevcut
         anahtarları (`tiktok.rmf.ts` → `ADSET_OPTIONS`). */
      const audienceIds = (briefPickedItems(options[BRIEF_GAP.TIKTOK_AUDIENCES]) ?? []).map((item) => item.id);
      const interestIds = (briefPickedItems(options[BRIEF_GAP.TIKTOK_INTERESTS]) ?? []).map((item) => item.id);
      return {
        ...(audienceIds.length > 0 ? { audienceIds } : {}),
        ...(interestIds.length > 0 ? { interestCategoryIds: interestIds } : {}),
        ...(optimization?.promotionType ? { promotionType: optimization.promotionType } : {}),
        /* Lead: `INSTANT_PAGE` — bağlayıcı Instant Form'lu reklamı YALNIZ bu
           sette kabul ediyor (`assertLeadFormAdGroup`). */
        ...(optimization?.promotionTargetType ? { promotionTargetType: optimization.promotionTargetType } : {}),
        placements: placement === 'AUTOMATIC' ? 'AUTOMATIC' : [placement],
      };
    }
    case AdPlatform.TWITTER: {
      /* Verilmezse adapter `ALL_ON_TWITTER` kullanıyor — aynı varsayılan. */
      const chosen = choiceValues(BRIEF_GAP.X_PLACEMENTS, X_BRIEF_PLACEMENTS, true, options[BRIEF_GAP.X_PLACEMENTS]);
      return chosen && chosen.length > 0 ? { placements: chosen } : null;
    }
    default:
      return null;
  }
}

/**
 * Reklam adımının alanları — platformun kabul ettiği KADARI.
 *
 *   META / LINKEDIN  başlık + metin + CTA + kimlik (+ lead formu).
 *   TIKTOK           🔴 başlık YOK (adapter reddediyor), metin 1–100
 *                    (sığmıyorsa `tiktok.adText`), CTA TikTok sözcüğüne
 *                    (+ Instant Form `leadFormRef`).
 *   TWITTER          🔴 CTA YOK (web sitesi kartında düğme yok, adapter
 *                    reddediyor); kart başlığı ≤70, gönderi ≤280.
 */
function publishFieldsFor(
  platform: AdPlatform,
  brief: CampaignBrief,
  options: Readonly<Record<string, unknown>>,
  identity: string | null,
  leadForm: BriefLeadFormPlan | null,
): Omit<PublishInput, 'targetId' | 'targetLevel'> {
  const identityRef = identity ? { identityRef: identity } : {};

  if (platform === AdPlatform.TIKTOK) {
    return {
      linkUrl: brief.linkUrl,
      bodyText: optionText(options, BRIEF_GAP.TIKTOK_AD_TEXT) ?? brief.bodyText,
      /* TikTok CTA'sız reklam kabul etmiyor; brief CTA'sı yoksa Meta
         adapter'ının varsayılanıyla AYNI (`LEARN_MORE`). */
      ctaType: brief.ctaType ? (TIKTOK_CTA_FOR_BRIEF[brief.ctaType] ?? brief.ctaType) : 'LEARN_MORE',
      ...identityRef,
      /* Instant Form → reklamın `page_id`si (bağlayıcı çeviriyor). */
      ...(leadForm?.mode === 'existing' ? { leadFormRef: leadForm.ref } : {}),
    };
  }

  if (platform === AdPlatform.TWITTER) {
    return {
      linkUrl: brief.linkUrl,
      headline: optionText(options, BRIEF_GAP.X_CARD_TITLE) ?? brief.headline,
      bodyText: optionText(options, BRIEF_GAP.X_POST_TEXT) ?? brief.bodyText,
      ...identityRef,
    };
  }

  return {
    linkUrl: brief.linkUrl,
    headline: brief.headline,
    bodyText: brief.bodyText,
    ...(brief.ctaType ? { ctaType: brief.ctaType } : {}),
    ...identityRef,
    ...(leadForm?.mode === 'existing' ? { leadFormRef: leadForm.ref } : {}),
  };
}

// ── Kitle ekleri ve Google katalog türleri (30 Eyl 2026) ─────────────────

/// Seçilen Google konumları — kimlik (sayı) + (varsa) ülke kodu.
function googleLocationsOf(options: Readonly<Record<string, unknown>>): { id: string; countryCode: string | null }[] {
  return (briefPickedItems(options[BRIEF_GAP.GOOGLE_LOCATIONS]) ?? []).flatMap((item) => {
    const id = /^(?:geoTargetConstants\/)?(\d{1,20})$/.exec(item.id)?.[1];
    return id ? [{ id, countryCode: item.extra?.countryCode?.toUpperCase() ?? null }] : [];
  });
}

/**
 * Seçilen şehir/bölgelerin KAPSADIĞI ülkeler dışarıda kalan ülke listesi.
 * Konumun ülkesi bilinmiyorsa (model düz kimlik yazdı) ülke DÜŞMÜYOR —
 * genişleme riski bilinçli: yanlış ülkeyi silmek reklamı hiç göstermemekti.
 */
function uncoveredCountries(countries: readonly string[], picked: readonly { countryCode: string | null }[]): string[] {
  const covered = new Set(picked.map((row) => row.countryCode).filter((code): code is string => code !== null));
  return countries.filter((country) => !covered.has(country.toUpperCase()));
}

/**
 * Meta hedefleme ekleri — adapter'ın `targeting_extras` anahtarı
 * (`meta.connector.ts` → hedeflemenin İÇİNE birleştiriliyor):
 *   `flexible_spec[0].interests`  ilgi alanları (Meta'nın önerdiği biçim)
 *   `custom_audiences`            özel + benzer kitleler
 *   `geo_locations`               yalnız şehir/bölge seçildiyse: kapsanan
 *                                 ülke DÜŞMÜŞ ülke listesi + şehir/bölge (Meta
 *                                 çakışan konumu reddediyor)
 */
function metaTargetingExtras(
  countries: readonly string[],
  options: Readonly<Record<string, unknown>>,
): Record<string, unknown> | null {
  const interests = briefPickedItems(options[BRIEF_GAP.META_INTERESTS]) ?? [];
  const audiences = briefPickedItems(options[BRIEF_GAP.META_AUDIENCES]) ?? [];
  const places = (briefPickedItems(options[BRIEF_GAP.META_LOCATIONS]) ?? [])
    .map((item) => metaGeoOf(item))
    .filter((row): row is NonNullable<ReturnType<typeof metaGeoOf>> => row !== null);

  const out: Record<string, unknown> = {};
  if (interests.length > 0) {
    out.flexible_spec = [{ interests: interests.map((item) => ({ id: item.id, ...(item.name ? { name: item.name } : {}) })) }];
  }
  if (audiences.length > 0) out.custom_audiences = audiences.map((item) => ({ id: item.id }));
  if (places.length > 0) {
    const rest = uncoveredCountries(countries, places);
    const cities = places.filter((row) => row.bucket === 'cities').map((row) => ({ key: row.key }));
    const regions = places.filter((row) => row.bucket === 'regions').map((row) => ({ key: row.key }));
    out.geo_locations = {
      ...(rest.length > 0 ? { countries: rest } : {}),
      ...(cities.length > 0 ? { cities } : {}),
      ...(regions.length > 0 ? { regions } : {}),
    };
  }
  return Object.keys(out).length > 0 ? out : null;
}

/// Uygulama kimliği + mağaza — cevap, yoksa brief'in mağaza adresi.
function resolvedGoogleApp(
  brief: CampaignBrief,
  options: Readonly<Record<string, unknown>>,
): { appId: string; store: 'GOOGLE_APP_STORE' | 'APPLE_APP_STORE' } | null {
  const derived = appFromStoreUrl(brief.linkUrl);
  const appId = optionText(options, BRIEF_GAP.GOOGLE_APP_ID) ?? derived?.appId ?? null;
  const rawStore = options[BRIEF_GAP.GOOGLE_APP_STORE];
  const store = (GOOGLE_APP_STORES as readonly unknown[]).includes(rawStore)
    ? (rawStore as 'GOOGLE_APP_STORE' | 'APPLE_APP_STORE')
    : (derived?.store ?? null);
  return appId && store ? { appId, store } : null;
}

/**
 * Brief yaş aralığı → Google yaş kovaları (ÇAKIŞANLAR hedefte; Demand Gen
 * ad group'unda `demographics` — işleyici geri kalanı dışlıyor). Aralık
 * verilmediyse `null` (hedefleme yok = herkes).
 */
const GOOGLE_AGE_BUCKETS: readonly { value: string; min: number; max: number }[] = [
  { value: 'AGE_RANGE_18_24', min: 18, max: 24 },
  { value: 'AGE_RANGE_25_34', min: 25, max: 34 },
  { value: 'AGE_RANGE_35_44', min: 35, max: 44 },
  { value: 'AGE_RANGE_45_54', min: 45, max: 54 },
  { value: 'AGE_RANGE_55_64', min: 55, max: 64 },
  { value: 'AGE_RANGE_65_UP', min: 65, max: 200 },
];

function googleDemographics(audience: BriefAudience): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (audience.ageMin !== undefined || audience.ageMax !== undefined) {
    const min = audience.ageMin ?? 0;
    const max = audience.ageMax === undefined || audience.ageMax >= OPEN_AGE ? 200 : audience.ageMax;
    const ranges = GOOGLE_AGE_BUCKETS.filter((bucket) => bucket.max >= min && bucket.min <= max).map((b) => b.value);
    if (ranges.length > 0 && ranges.length < GOOGLE_AGE_BUCKETS.length) out.ageRanges = ranges;
  }
  if (audience.genders.length === 1) out.genders = [audience.genders[0] === 'male' ? 'MALE' : 'FEMALE'];
  return Object.keys(out).length > 0 ? out : null;
}

/// Amaç × tür → teklif stratejisi (işleyicinin `BIDDING_BY_TYPE` tablosunda geçerli olanlar).
function googleBidding(goal: CampaignGoal, type: 'PERFORMANCE_MAX' | 'DEMAND_GEN' | 'SHOPPING' | 'APP'): { type: string } {
  switch (type) {
    case 'PERFORMANCE_MAX':
      /* PMax dönüşüm tabanlı teklif İSTİYOR; satışta değer, diğerlerinde adet. */
      return { type: goal === 'sales' ? 'MAXIMIZE_CONVERSION_VALUE' : 'MAXIMIZE_CONVERSIONS' };
    case 'DEMAND_GEN':
      return { type: goal === 'sales' || goal === 'leads' ? 'MAXIMIZE_CONVERSIONS' : 'MAXIMIZE_CLICKS' };
    case 'SHOPPING':
      /* Dönüşüm izlemesi olmadan da çalışan tek güvenli seçim. */
      return { type: 'MAXIMIZE_CLICKS' };
    case 'APP':
      return { type: 'MAXIMIZE_CONVERSIONS' };
  }
}

type GoogleCatalogBuild =
  | { ok: true; params: Record<string, unknown> }
  | { ok: false; missing: BriefGapField[]; blocked: BriefNoticeCode | null };

/**
 * Brief → `google.campaign.create` parametreleri (arama DIŞI türler).
 *
 * Sınırlar operasyon şemasından (`googleCampaignCreateSchema`) — burada
 * yakalanan her ihlal `missing`e ya da engele dönüşüyor; atomik istek
 * platformda düşseydi hiçbir şey açılmazdı ama kullanıcı sebebini bizim
 * cümlemizle değil Google'ınkiyle okurdu.
 *
 * Görseller: brief kreatifleri ORANINA göre yuvaya (`googleImageSlot`).
 * Boyut bilinmiyorsa (`context.dims` yok — ajan kartının kuru denetimi)
 * görsel denetimi YAPILMIYOR; yayınlayıcı bağlamla yeniden derliyor.
 * Videolar Google'a buradan yüklenemiyor (YouTube işleme asenkron) — YouTube
 * türü hesaptaki YOUTUBE_VIDEO varlıklarını kullanıyor.
 */
function googleCatalogOp(
  brief: CampaignBrief,
  placement: BriefPlacement,
  type: GoogleBriefCampaignType,
  catalogType: 'PERFORMANCE_MAX' | 'DEMAND_GEN' | 'SHOPPING' | 'APP',
  endTime: string | undefined,
  context: BriefContext,
): GoogleCatalogBuild {
  const options = placement.options;
  const missing: BriefGapField[] = [];
  const headlines = optionList(options, BRIEF_GAP.GOOGLE_HEADLINES);
  const descriptions = optionList(options, BRIEF_GAP.GOOGLE_DESCRIPTIONS);
  const longHeadlines = optionList(options, BRIEF_GAP.GOOGLE_LONG_HEADLINES);
  const businessName = optionText(options, BRIEF_GAP.GOOGLE_BUSINESS_NAME);
  const logoRaw = optionText(options, BRIEF_GAP.GOOGLE_LOGO);
  const logo = logoRaw ? (UUID_RE.test(logoRaw) ? { creativeId: logoRaw } : { assetRef: logoRaw }) : null;

  if (catalogType !== 'SHOPPING') {
    if (headlines.some((text) => text.length > GOOGLE_HEADLINE_MAX)) missing.push(BRIEF_GAP.GOOGLE_HEADLINES);
    if (descriptions.some((text) => text.length > GOOGLE_DESCRIPTION_MAX)) missing.push(BRIEF_GAP.GOOGLE_DESCRIPTIONS);
    /* Performance Max: açıklamalardan en az biri ≤60 (Google kuralı). */
    if (
      catalogType === 'PERFORMANCE_MAX' &&
      !descriptions.some((text) => text.length <= 60) &&
      !missing.includes(BRIEF_GAP.GOOGLE_DESCRIPTIONS)
    ) {
      missing.push(BRIEF_GAP.GOOGLE_DESCRIPTIONS);
    }
  }
  if (missing.length > 0) return { ok: false, missing, blocked: null };

  /* Görseller — oranına göre yuva. */
  const slots: Record<GoogleImageSlot, { creativeId: string }[]> = {
    LANDSCAPE: [],
    SQUARE: [],
    PORTRAIT: [],
    TALL_PORTRAIT: [],
  };
  const dims = context.dims;
  for (const id of brief.creativeIds) {
    if (context.media && context.media[id] !== undefined && context.media[id] !== 'image') continue;
    const size = dims?.[id];
    const slot = size ? googleImageSlot(size.width, size.height) : null;
    if (slot) slots[slot].push({ creativeId: id });
  }
  const imageCount = slots.LANDSCAPE.length + slots.SQUARE.length + slots.PORTRAIT.length + slots.TALL_PORTRAIT.length;
  if (dims) {
    const pmaxShort = catalogType === 'PERFORMANCE_MAX' && (slots.LANDSCAPE.length === 0 || slots.SQUARE.length === 0);
    const dgShort = type === 'DEMAND_GEN' && slots.LANDSCAPE.length + slots.SQUARE.length === 0;
    if (pmaxShort || dgShort) {
      return { ok: false, missing: [], blocked: BRIEF_NOTICE.GOOGLE_ASSETS_MISSING };
    }
  }

  /* Konum — ülke (kapsanmayan) + şehir/bölge; hiçbiri yoksa alan YOK
     (Google'da "tüm ülkeler" — brief ülkesiz bırakmayı zaten açıkça seçti). */
  const locations = googleLocationsOf(options);
  const countries = uncoveredCountries(brief.audience.countries, locations);
  const include = [
    ...countries.map((country) => ({ country })),
    ...locations.map((row) => ({ geoTargetConstant: row.id })),
  ];
  const userListRefs = (briefPickedItems(options[BRIEF_GAP.GOOGLE_USER_LISTS]) ?? []).map((item) => item.id);
  const userInterestIds = (briefPickedItems(options[BRIEF_GAP.GOOGLE_INTERESTS]) ?? []).map((item) => item.id);
  const audiences =
    userListRefs.length > 0 || userInterestIds.length > 0
      ? {
          ...(userListRefs.length > 0 ? { userListRefs } : {}),
          ...(userInterestIds.length > 0 ? { userInterestIds } : {}),
        }
      : null;
  const demographics = googleDemographics(brief.audience);

  const params: Record<string, unknown> = {
    name: brief.name,
    type: catalogType,
    budget: { daily: placement.dailyBudget },
    bidding: googleBidding(brief.goal, catalogType),
    ...(endTime ? { endDate: endTime } : {}),
    ...(include.length > 0 ? { geo: { include } } : {}),
  };

  switch (type) {
    case 'PERFORMANCE_MAX':
      params.pmax = {
        assetGroups: [
          {
            name: brief.name,
            finalUrls: [brief.linkUrl],
            headlines: headlines.slice(0, 15),
            longHeadlines: longHeadlines.slice(0, 5),
            descriptions: descriptions.slice(0, 5),
            ...(businessName ? { businessName } : {}),
            ...(logo ? { logos: [logo] } : {}),
            marketingImages: slots.LANDSCAPE.slice(0, 20),
            squareMarketingImages: slots.SQUARE.slice(0, 20),
            ...(slots.PORTRAIT.length > 0 ? { portraitMarketingImages: slots.PORTRAIT.slice(0, 20) } : {}),
            /* Arama kelimeleri PMax'e "arama teması" sinyali (isteğe bağlı). */
            ...(optionList(options, BRIEF_GAP.GOOGLE_KEYWORDS).length > 0
              ? { searchThemes: optionList(options, BRIEF_GAP.GOOGLE_KEYWORDS).map(stripMatchSyntax).slice(0, 50) }
              : {}),
          },
        ],
      };
      break;
    case 'DEMAND_GEN':
      params.adGroups = [
        {
          name: brief.name,
          ...(demographics ? { demographics } : {}),
          ...(audiences ? { audiences } : {}),
          ads: [
            {
              format: 'DEMAND_GEN_MULTI_ASSET',
              ...(slots.LANDSCAPE.length > 0 ? { marketingImages: slots.LANDSCAPE.slice(0, 20) } : {}),
              ...(slots.SQUARE.length > 0 ? { squareImages: slots.SQUARE.slice(0, 20) } : {}),
              ...(slots.PORTRAIT.length > 0 ? { portraitImages: slots.PORTRAIT.slice(0, 20) } : {}),
              ...(slots.TALL_PORTRAIT.length > 0 ? { tallPortraitImages: slots.TALL_PORTRAIT.slice(0, 20) } : {}),
              ...(logo ? { logos: [logo] } : {}),
              headlines: headlines.slice(0, 5),
              descriptions: descriptions.slice(0, 5),
              ...(businessName ? { businessName } : {}),
              finalUrl: brief.linkUrl,
            },
          ],
        },
      ];
      break;
    case 'YOUTUBE': {
      const videos = (briefPickedItems(options[BRIEF_GAP.GOOGLE_YOUTUBE_VIDEOS]) ?? []).map((item) => ({
        youtubeVideoId: item.id,
      }));
      params.adGroups = [
        {
          name: brief.name,
          /* 🔴 Yalnız üç YouTube kanalı açık = YouTube'a özel Demand Gen
             (Google Ads API'nin VIDEO kampanyası karşılığı). */
          channels: {
            youtubeInStream: true,
            youtubeInFeed: true,
            youtubeShorts: true,
            discover: false,
            gmail: false,
            display: false,
          },
          ...(demographics ? { demographics } : {}),
          ...(audiences ? { audiences } : {}),
          ads: [
            {
              format: 'DEMAND_GEN_VIDEO',
              videos: videos.slice(0, 5),
              ...(logo ? { logos: [logo] } : {}),
              ...(businessName ? { businessName } : {}),
              headlines: headlines.slice(0, 5),
              longHeadlines: longHeadlines.slice(0, 5),
              descriptions: descriptions.slice(0, 5),
              finalUrl: brief.linkUrl,
              ...(brief.ctaType && GOOGLE_VIDEO_CTAS.has(brief.ctaType) ? { callToAction: brief.ctaType } : {}),
            },
          ],
        },
      ];
      break;
    }
    case 'SHOPPING': {
      const merchant = optionText(options, BRIEF_GAP.GOOGLE_MERCHANT);
      params.shopping = { merchantId: Number(merchant) };
      params.adGroups = [
        { name: brief.name, type: 'SHOPPING_PRODUCT_ADS', ads: [{ format: 'SHOPPING_PRODUCT' }] },
      ];
      break;
    }
    case 'APP': {
      const app = resolvedGoogleApp(brief, options);
      params.app = { subtype: 'INSTALLS', appId: app?.appId ?? '', store: app?.store ?? 'GOOGLE_APP_STORE' };
      params.adGroups = [
        {
          name: brief.name,
          ads: [
            {
              format: 'APP',
              headlines: headlines.slice(0, 5),
              descriptions: descriptions.slice(0, 5),
              ...(imageCount > 0
                ? {
                    images: [...slots.LANDSCAPE, ...slots.SQUARE, ...slots.PORTRAIT, ...slots.TALL_PORTRAIT].slice(0, 20),
                  }
                : {}),
            },
          ],
        },
      ];
      break;
    }
    default:
      break;
  }
  return { ok: true, params };
}

/// Google video reklamı CTA'ları (`google.ops.ts` → `callToAction` enum'unun brief'le kesişimi).
const GOOGLE_VIDEO_CTAS: ReadonlySet<string> = new Set([
  'LEARN_MORE',
  'SIGN_UP',
  'CONTACT_US',
  'SUBSCRIBE',
  'DOWNLOAD',
  'BOOK_NOW',
  'SHOP_NOW',
  'BUY_NOW',
  'ORDER_NOW',
  'SEE_MORE',
  'WATCH_NOW',
]);

/// Arama kelimesinin eşleme sözdizimi (`[…]`, `+…`, `"…"`) PMax arama temasında yok.
function stripMatchSyntax(keyword: string): string {
  return keyword.replace(/^\[(.*)\]$/, '$1').replace(/^"(.*)"$/, '$1').replace(/^\+/, '').trim().slice(0, 80);
}

// ═══════════════════════════════════════════════════════════════════════════
// Plan — son adımın beslendiği liste
// ═══════════════════════════════════════════════════════════════════════════

/** Plan hesabı için hesabın bilinmesi gereken kadarı. */
export interface BriefAccountRef {
  id: string;
  /**
   * Hesabın BAĞLANTI kimliği.
   *
   * Marka kimliği listesi (`GET /integrations/connections/:id/identities`)
   * bunu istiyor. Arayüz Meta Sayfa kimliğini kullanıcıya YAZDIRMIYOR,
   * seçtiriyor: 25 Ağu 2026'da bir kullanıcı `profile.php?id=…` adresinden
   * kimlik yapıştırdı, o kimlik bir Sayfa değildi ve bunu ancak Meta reklamı
   * reddedince öğrenebildik (`PlatformIdentity` başlığı).
   */
  connectionId: string;
  platform: AdPlatform;
  name: string;
  currency: string;
  /// `canPlatformCreate(card.capabilities)` — KODA GÖMÜLMÜYOR, yetenek
  /// kartından geliyor. Adapter yazıldığı gün ekran yalan söylemesin.
  canCreate: boolean;
}

/** Son adımda bir platform satırı. */
export interface BriefPlatformPlan {
  adAccountId: string;
  /// Kimlik listesini çekmek için — bkz. `BriefAccountRef.connectionId`.
  connectionId: string;
  platform: AdPlatform;
  accountName: string;
  currency: string;
  /// Platformun kendi hedef sözcüğü; amaç bu platformda karşılıksızsa `null`.
  objective: string | null;
  /// Kutucuk işaretlenebilir mi.
  eligible: boolean;
  /// Seçilirse sorulacak alanlar.
  gaps: BriefGap[];
  notices: BriefNotice[];
  /// Eşit bölümden düşen pay — başlangıç değeri, kullanıcı değiştirebiliyor.
  suggestedDailyBudget: number;
}

export interface BriefPlanResponse {
  dailyBudgetTotal: number;
  plans: BriefPlatformPlan[];
}

/**
 * Hesap listesini son adımın satırlarına çevir.
 *
 * ⚠️ UYGUN OLMAYAN HESAPLAR DA LİSTEDE. Gizlemek "hesabım nerede" sorusunu
 * doğuruyor (`WIZARD_STEPS.account` yorumunda aynı karar); satır sönük durur
 * ve `notices` sebebini söyler.
 *
 * Bütçe bölümü YALNIZ uygun olanlar arasında yapılıyor: seçilemeyecek bir
 * platforma pay ayırmak, kullanıcının toplamının bir kısmını görünmez bir
 * yere göndermek olurdu.
 */
export function planBrief(
  brief: CampaignBrief,
  accounts: readonly BriefAccountRef[],
  context: BriefContext = {},
): BriefPlanResponse {
  /* Önce her satırın ENGELLERİ — bütçe yalnız engelsiz satırlara bölünüyor
     (29 Eyl 2026, Faz 5: ülke/yaş/medya/emoji engelleri de sayılıyor; önceden
     yalnız "açamaz" ve "amaç yok" sayılıyordu ve pay, açılamayacak bir
     platforma gidiyordu). */
  const rows = accounts.map((account) => {
    const objective = objectiveFor(brief.goal, account.platform);
    const notices: BriefNotice[] = [];
    const platform = account.platform;

    if (!account.canCreate) {
      notices.push({ code: BRIEF_NOTICE.CANNOT_CREATE, platform, blocking: true });
    } else if (objective === null) {
      notices.push({ code: BRIEF_NOTICE.GOAL_UNSUPPORTED, platform, blocking: true });
    } else {
      const structural = structuralBlock(brief, platform);
      if (structural) notices.push({ code: structural, platform, blocking: true });
      notices.push(...mediaNotices(brief, platform, context));

      /* 🔴 Görsellerin kullanılmayacağı SEÇİMDEN ÖNCE söyleniyor. Sonradan
         söylemek, kullanıcının parasını beklediğinden başka bir reklama
         harcaması demek. */
      /* 30 Eyl 2026: yalnız amacın VARSAYILAN Google türü arama ise — görsel
         kullanan türler (PMax, Demand Gen) seçilebiliyor ve tanınırlık/uygulama
         amaçlarında arama hiç yok. */
      if (platform === AdPlatform.GOOGLE && GOOGLE_TYPES_BY_GOAL[brief.goal][0] === 'SEARCH') {
        notices.push({ code: BRIEF_NOTICE.GOOGLE_TEXT_ONLY, platform, blocking: false });
      }
      /* Potansiyel müşteri X'te yerel form değil web sitesi dönüşümü —
         kullanıcı SEÇİMDEN ÖNCE bilmeli (`GOAL_OBJECTIVE` başlığı). */
      if (brief.goal === 'leads' && platform === AdPlatform.TWITTER) {
        notices.push({ code: BRIEF_NOTICE.LEADS_AS_WEBSITE_CONVERSION, platform, blocking: false });
      }
      /* TikTok: form SEÇİLİYOR, burada kurulamıyor — formu olmayan kullanıcı
         nereye gideceğini listeyi boş görmeden ÖNCE bilmeli. */
      if (brief.goal === 'leads' && platform === AdPlatform.TIKTOK) {
        notices.push({ code: BRIEF_NOTICE.TIKTOK_LEAD_FORM_IN_ADS_MANAGER, platform, blocking: false });
      }
    }

    return { account, objective, notices, eligible: !notices.some((notice) => notice.blocking) };
  });

  const eligible = rows.filter((row) => row.eligible);
  const shares = splitDailyBudget(brief.dailyBudgetTotal, eligible.length);
  const shareByAccount = new Map(eligible.map((row, index) => [row.account.id, shares[index] ?? 0]));

  const plans = rows.map(({ account, objective, notices, eligible: ok }): BriefPlatformPlan => {
    const share = shareByAccount.get(account.id) ?? 0;
    if (share > 0 && share < BUDGET_SHARE_WARN_THRESHOLD) {
      notices.push({ code: BRIEF_NOTICE.BUDGET_SHARE_LOW, platform: account.platform, blocking: false });
    }

    return {
      adAccountId: account.id,
      connectionId: account.connectionId,
      platform: account.platform,
      accountName: account.name,
      currency: account.currency,
      objective,
      eligible: ok,
      gaps: objective !== null ? briefGaps(brief.goal, account.platform, brief) : [],
      notices,
      suggestedDailyBudget: share,
    };
  });

  return { dailyBudgetTotal: brief.dailyBudgetTotal, plans };
}

/**
 * Plan satırının medya uyarıları. TikTok'ta görsel ENGEL DEĞİL (Pangle
 * seçilebilir) — engel, derlemede seçilen yerleşimle birlikte veriliyor.
 */
function mediaNotices(brief: CampaignBrief, platform: AdPlatform, context: BriefContext): BriefNotice[] {
  const media = context.media;
  if (!media || platform === AdPlatform.GOOGLE) return [];
  const kinds = brief.creativeIds.map((id) => media[id]).filter((kind) => kind !== undefined);

  if (platform === AdPlatform.TIKTOK) {
    const other = kinds.filter((kind) => kind === 'other').length;
    const images = kinds.filter((kind) => kind === 'image').length;
    const out: BriefNotice[] = [];
    if (other > 0) out.push({ code: BRIEF_NOTICE.MEDIA_UNSUPPORTED, platform, blocking: true, params: { count: other } });
    if (images > 0) {
      out.push({ code: BRIEF_NOTICE.TIKTOK_VIDEO_REQUIRED, platform, blocking: false, params: { count: images } });
    }
    return out;
  }

  const bad = unsupportedMedia(brief, platform, {}, context).length;
  return bad > 0 ? [{ code: BRIEF_NOTICE.MEDIA_UNSUPPORTED, platform, blocking: true, params: { count: bad } }] : [];
}

/**
 * "Bu pay çok küçük" uyarısının eşiği.
 *
 * 🔴 BU BİR REDDİN EŞİĞİ DEĞİL, bir UYARININ eşiği — ve para biriminden
 * bağımsız olduğu için kesin bir şey iddia etmiyor. Gerçek alt sınır HESABA
 * göre değişiyor (Meta `min_daily_budget` döndürüyor: By Nini hesabında
 * 47,88 TRY) ve kesin reddi platform veriyor. Buradaki sayı yalnız "üç
 * platforma bölünce her birine 3 lira düştü" durumunu kullanıcıya göstermek
 * için var.
 */
export const BUDGET_SHARE_WARN_THRESHOLD = 20;

// ═══════════════════════════════════════════════════════════════════════════
// Yayınlama defteri — platform başına AYRI sonuç
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir platformun bu turdaki sonucu.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 NEDEN TEK BİR BAŞARI/HATA YETMİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Fan-out'ta kısmi arıza NORMAL durumdur: Meta açılır, Google kelimeyi
 * reddeder. Tek bir `ok: false` dönseydi kullanıcı, Meta'da GERÇEK bir
 * kampanyası olduğunu bilmeden baştan başlardı — ve ikinci bir kampanya
 * açardı.
 *
 * Aynı şey bir platformun İÇİNDE de geçerli: kampanya açıldı, ad set açıldı,
 * üç reklamdan ikisi yayınlandı. `adIds` o ikisini taşıyor ve `state`
 * `partial` diyor; kullanıcı neyin gerçek olduğunu okuyabiliyor.
 */
export const BRIEF_PLACEMENT_STATE = {
  /// Kampanya + ad set + reklamların HEPSİ açıldı. Hepsi PAUSED.
  CREATED: 'created',
  /// Bir kısmı açıldı. `campaignId`/`adSetId`/`adIds` nereye kadar gidildiğini
  /// söylüyor ve o varlıklar GERÇEK — tekrar denemek yenisini açmamalı.
  PARTIAL: 'partial',
  /// Hiçbir şey açılmadı.
  FAILED: 'failed',
  /// Derleme aşamasında elendi (amaç desteklenmiyor, alan eksik). Platforma
  /// hiç GİDİLMEDİ — bu bir platform hatası değil, bir girdi hatası.
  SKIPPED: 'skipped',
} as const;
export type BriefPlacementState =
  (typeof BRIEF_PLACEMENT_STATE)[keyof typeof BRIEF_PLACEMENT_STATE];

export interface BriefPlacementResult {
  adAccountId: string;
  platform: AdPlatform;
  state: BriefPlacementState;
  /// BİZİM kimliğimiz. `null` = o adıma hiç gelinmedi ya da yerel satır
  /// yazılamadı (`RmfCreateResult.localRecordWritten`).
  campaignId: string | null;
  adSetId: string | null;
  /// Yayınlanmış reklamlar. Her kreatif bir reklam.
  adIds: string[];
  /**
   * Hata KODU — ham sağlayıcı metni değil.
   *
   * Ekran bunu `rmf.error.<code>` anahtarına çeviriyor; karşılığı yoksa
   * `errorMessage` basılıyor. Sağlayıcının ham cümlesini doğrudan kullanıcıya
   * göstermemek projede daha önce verilmiş bir karar.
   */
  errorCode: string | null;
  errorMessage: string | null;
  /// Derlemede elenmişse hangi alanlar eksikti — arayüz o satırı açıp
  /// kullanıcıyı eksik girdiye götürüyor.
  missing: BriefGapField[];
  /**
   * Derlemede YAPISAL bir engelle elendiyse hangisi (`BRIEF_NOTICE` kodu —
   * arayüz plan uyarısının çevirisini kullanıyor). `errorCode` o durumda
   * `BRIEF_PLACEMENT_BLOCKED` (amaç engelinde `BRIEF_GOAL_UNSUPPORTED_ON_PLATFORM`).
   */
  blocked?: BriefNoticeCode | null;
  /**
   * KATALOG YOLUYLA açılanların PLATFORM kimlikleri (30 Eyl 2026 — Google
   * arama dışı türler). O yol yerel kayıt YAZMIYOR (`PlatformOpsService`
   * senkronu tetikliyor): `campaignId`/`adSetId`/`adIds` bu yüzden boş ve
   * yayına alma hedefi (`activationTargets`) yok. Uydurma bir yerel kimlik
   * yazmak yerine platformun kimlikleri burada.
   */
  platformRefs?: { level: string; platformRef: string }[];
  /**
   * `true`: varlıklar platformda GERÇEKTEN açıldı (PAUSED) ama panelde
   * senkrondan sonra görünecek; yayına alma kampanya listesinden
   * (`google.campaign.set_status` ile `enableChildren`).
   */
  pendingSync?: boolean;
}

/**
 * BRIEF AKIŞININ KENDİ HATA KODLARI.
 *
 * `RmfErrorCode` PLATFORM çağrısının sonucunu anlatıyor (yetki düştü, kota
 * doldu, platform reddetti). Buradaki üç kod ise platforma HİÇ GİDİLMEDEN
 * verilen kararlar — girdi hataları. Aynı kümeye koymak, kullanıcıya
 * "platform reddetti" dedirtip olmayan bir sorunu aratmak olurdu.
 */
export const BriefErrorCode = {
  /// Seçilen platformun eksik alanları cevaplanmadı (`missing` doluyor).
  GAPS_UNANSWERED: 'BRIEF_GAPS_UNANSWERED',
  /// Seçilen amacın bu platformda karşılığı yok (`GOAL_OBJECTIVE` → `null`).
  GOAL_UNSUPPORTED_ON_PLATFORM: 'BRIEF_GOAL_UNSUPPORTED_ON_PLATFORM',
  /// Gönderilen hesap bu workspace'te yok. Kiracı yalıtımı sorguda; buraya
  /// düşmek başkasının hesabının gönderildiği anlamına geliyor.
  AD_ACCOUNT_NOT_FOUND: 'BRIEF_AD_ACCOUNT_NOT_FOUND',
  /// Yapısal engel (ülke yok, yaş kovasız, kreatif türü uygun değil, adda
  /// emoji) — hangisi olduğu `BriefPlacementResult.blocked`ta.
  PLACEMENT_BLOCKED: 'BRIEF_PLACEMENT_BLOCKED',
  /**
   * Yeni lead formu oluşturma YÖNETİCİ ister (geri alınamaz / varış adresi
   * taşıyan yazma — REST `/platform-ops/run` ile aynı çizgi). Platforma hiç
   * gidilmedi; mevcut bir form seçilebilir ya da yönetici yayınlar.
   */
  LEAD_FORM_CREATE_FORBIDDEN: 'BRIEF_LEAD_FORM_CREATE_FORBIDDEN',
} as const;
export type BriefErrorCode = (typeof BriefErrorCode)[keyof typeof BriefErrorCode];

export interface BriefLaunchResponse {
  results: BriefPlacementResult[];
  /**
   * `autoOptimizeBudget` açıkken kurulan `budget_redistribute` kuralının
   * kimliği.
   *
   * `null` iki farklı şey demek olabilir ve ikisi de meşru: kullanıcı izni
   * vermedi, ya da izin verdi ama TEK platform açıldı — tek kampanya arasında
   * bütçe kaydırmanın anlamı yok ve kural kurmak, hiç tetiklenmeyecek bir
   * satırı kullanıcının kural listesine bırakmak olurdu.
   */
  optimizerWorkflowId: string | null;
  /**
   * Yayına alınabilecek hedefler — `POST /campaigns/status` gövdesine AYNEN
   * giriyor.
   *
   * Sunucu hesaplıyor çünkü "hangi seviyeler, hangi sırayla" sorusunun cevabı
   * bu akışın bilgisi: üç seviye BİRDEN çevrilmeli (yalnız kampanyayı ACTIVE
   * yapmak, altı duraklatılmış kaldığı için hiçbir şey yayınlamaz) ve HER
   * reklam listeye girmeli.
   */
  activationTargets: { level: 'CAMPAIGN' | 'ADSET' | 'AD'; id: string }[];
}
